import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, Clock, Users, Video, Plus, X, Edit2, Trash2, MapPin, User, ExternalLink, FileText, ChevronUp, ChevronDown, FileAudio, Download, Loader, Mic, Upload, CheckCircle, AlertCircle, Pencil, Check, Square, Loader2, GripVertical, Star, LayoutGrid, List, Kanban, Folder, Settings, ArrowLeft, StickyNote, TrendingUp, ChevronLeft, ChevronRight, Sparkles, Search, ArrowUpDown, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiCall, mediaAPI, apiUpload } from '../services/api';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';
import ProjectDetailPanel from '../components/ProjectDetailPanel';

// Convert 24-hour time to 12-hour format with AM/PM
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${ampm}`;
};

// Parse a date string as local noon to avoid UTC-midnight timezone day-shift issues.
// "2025-06-15" via new Date() is UTC midnight → displays as June 14 in UTC-5 zones.
// Parsing as "2025-06-15T12:00:00" makes it local noon so it always shows as June 15.
const parseLocalDate = (dateStr) => {
  if (!dateStr || dateStr === 'TBD' || dateStr === '-' || dateStr === '') return null;
  const datePart = String(dateStr).split('T')[0].split(' ')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(datePart + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
};

const RealEstateManagingPartnerMeetings = () => {
  const location = useLocation();
  
  // Get user from auth context
  const { user } = useAuth();
  const userId = user?.user_id || user?.email || '';
  
  // Demo mode support
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  
  // API URL Configuration - always use same origin
  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const [meetings, setMeetings] = useState([]);
  const [smartsheetMeetings, setSmartsheetMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedOrgMeetings, setSelectedOrgMeetings] = useState([]); // Store all meetings for selected org
  const [meetingTranscriptions, setMeetingTranscriptions] = useState({}); // Map meeting ID to transcription ID
  const [showTranscriptionPopup, setShowTranscriptionPopup] = useState(false);
  const [viewingTranscription, setViewingTranscription] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first (default)
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [draggedMeeting, setDraggedMeeting] = useState(null);
  const [mainTab, setMainTab] = useState('all'); // 'all', 'projects', 'meetings' - Main tab filter
  const [searchQuery, setSearchQuery] = useState(''); // Search query for meetings
  const [transcriptionPage, setTranscriptionPage] = useState(1); // Pagination for transcriptions
  const [expandedTranscripts, setExpandedTranscripts] = useState({}); // Track expanded transcript cards
  const [expandedMeetings, setExpandedMeetings] = useState({}); // Track expanded meeting cards
  const [loadedBreakdowns, setLoadedBreakdowns] = useState({}); // Cache for loaded AI breakdowns
  const [loadingBreakdowns, setLoadingBreakdowns] = useState({}); // Track which breakdowns are loading
  const TRANSCRIPTIONS_PER_PAGE = 50;
  
  // Conversation edit/delete state
  const [editingConversation, setEditingConversation] = useState(null);
  const [showConversationMenu, setShowConversationMenu] = useState(null);
  const [conversationEditMode, setConversationEditMode] = useState(false);
  const [editConversationData, setEditConversationData] = useState({ title: '', category: '' });
  
  // Meeting status state (upcoming, in_progress, done)
  const [meetingStatuses, setMeetingStatuses] = useState(() => {
    const saved = localStorage.getItem('meeting_statuses');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Transcription states
  const [conversations, setConversations] = useState([]);
  const [selectedTranscriptCategory, setSelectedTranscriptCategory] = useState('all');
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [isUploadExpanded, setIsUploadExpanded] = useState(false);
  const [draggedTranscription, setDraggedTranscription] = useState(null);
  
  // Excel upload states
  const [dataSource, setDataSource] = useState('personal'); // 'personal' only - Default to Personal
  const [excelFile, setExcelFile] = useState(null);
  const [excelMeetings, setExcelMeetings] = useState([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [personalMeetings, setPersonalMeetings] = useState([]);
  
  // Drag and drop for reordering within columns
  const [draggedCardIndex, setDraggedCardIndex] = useState(null);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioLevels, setAudioLevels] = useState(Array(12).fill(0));
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [language, setLanguage] = useState('');
  
  // Transcription panel states
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [conversationFilter, setConversationFilter] = useState('all');
  const [draggedConversation, setDraggedConversation] = useState(null);
  
  // Google Sheets sync states
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [showSheetsModal, setShowSheetsModal] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('Sheet1');
  const [sheetConnections, setSheetConnections] = useState([]);
  const [syncingSheets, setSyncingSheets] = useState(false);
  
  // Document upload states
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [documentUploadFile, setDocumentUploadFile] = useState(null);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(null);
  const [parsedMeetingsFromDoc, setParsedMeetingsFromDoc] = useState(null);
  const documentFileInputRef = useRef(null);
  
  // Document upload states for meetings
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentUploadMeetingId, setDocumentUploadMeetingId] = useState(null);
  
  const supportedFormats = ['mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm'];
  const supportedDocFormats = ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'mp4', 'mov', 'avi', 'webm', 'mkv'];

  // Default meeting categories
  const defaultMeetingCategories = [
    { id: 'all', name: 'All', icon: '📋', color: 'gray' },
    { id: 'real-estate', name: 'Real Estate', icon: '🏢', color: 'blue' },
    { id: 'investments', name: 'Investments', icon: '💰', color: 'green' },
    { id: 'speeches', name: 'Speeches', icon: '🎤', color: 'purple' },
    { id: 'legal', name: 'Legal', icon: '⚖️', color: 'red' }
  ];
  
  // Custom meeting categories - loaded from DB
  const [customMeetingCategories, setCustomMeetingCategories] = useState(defaultMeetingCategories);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Editable category state
  const [showMeetingCategoryModal, setShowMeetingCategoryModal] = useState(false);
  const [newMeetingCategoryName, setNewMeetingCategoryName] = useState('');
  const [newMeetingCategoryIcon, setNewMeetingCategoryIcon] = useState('📅');
  const [newMeetingCategoryColor, setNewMeetingCategoryColor] = useState('blue');
  const [editingMeetingCategory, setEditingMeetingCategory] = useState(null);
  const [editMeetingCategoryValue, setEditMeetingCategoryValue] = useState('');
  
  // Available emoji icons for meeting categories
  const meetingCategoryIcons = ['📋', '🏢', '💰', '🎤', '⚖️', '👤', '📅', '💼', '🤝', '📊', '🎯', '💡', '🔔', '⏰', '📱', '💻', '🏠', '✈️', '🎓', '⚽', '🎨', '🎵', '🍽️', '🛒', '⚕️', '🔧', '📁', '🌟', '🎉', '📧'];
  
  // Available colors for categories
  const meetingCategoryColors = ['blue', 'green', 'purple', 'red', 'cyan', 'orange', 'pink', 'yellow', 'indigo', 'teal', 'emerald', 'gray'];
  
  // Category management functions
  const addMeetingCategory = async () => {
    if (!newMeetingCategoryName.trim()) return;
    
    const categoryId = newMeetingCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    if (customMeetingCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    try {
      const res = await fetch(`${apiUrl}/api/project-categories/?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newMeetingCategoryName,
          icon: newMeetingCategoryIcon,
          color: newMeetingCategoryColor
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCustomMeetingCategories(prev => [...prev, data.category]);
        setNewMeetingCategoryName('');
        setNewMeetingCategoryIcon('📅');
        setNewMeetingCategoryColor('blue');
        toast.success(`Added "${newMeetingCategoryName}" category`);
      } else {
        toast.error(data.detail || 'Failed to add category');
      }
    } catch (err) {
      toast.error('Failed to add category');
    }
  };
  
  const updateMeetingCategory = async (oldId, newName, newIcon, newColor) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    try {
      const res = await fetch(`${apiUrl}/api/project-categories/${encodeURIComponent(oldId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName, icon: newIcon, color: newColor })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCustomMeetingCategories(prev => prev.map(c => {
          if (c.id === oldId) {
            return { ...c, id: newId, name: newName, icon: newIcon || c.icon, color: newColor || c.color };
          }
          return c;
        }));
        if (selectedCategory === oldId) setSelectedCategory(newId);
        // Also handle if selectedCategory was stored as the old NAME
        const oldName = customMeetingCategories.find(c => c.id === oldId)?.name;
        if (oldName && selectedCategory === oldName) setSelectedCategory(newName);
        setEditingMeetingCategory(null);
        setEditMeetingCategoryValue('');
        toast.success(`Category renamed to "${newName}"`);
      } else {
        toast.error(data.detail || 'Failed to update category');
      }
    } catch (err) {
      toast.error('Failed to update category');
    }
  };
  
  const deleteMeetingCategory = async (categoryId) => {
    const protectedCategories = ['all', 'real-estate'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    try {
      const res = await fetch(`${apiUrl}/api/project-categories/${encodeURIComponent(categoryId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCustomMeetingCategories(prev => prev.filter(c => c.id !== categoryId));
        if (selectedCategory === categoryId) setSelectedCategory('all');
        toast.success('Category deleted');
      } else {
        toast.error(data.detail || 'Failed to delete category');
      }
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };
  
  // Get color classes for category
  const getMeetingCategoryColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 border-blue-500',
      green: 'bg-green-600 border-green-500',
      purple: 'bg-purple-600 border-purple-500',
      red: 'bg-red-600 border-red-500',
      cyan: 'bg-cyan-600 border-cyan-500',
      orange: 'bg-orange-600 border-orange-500',
      pink: 'bg-pink-600 border-pink-500',
      yellow: 'bg-yellow-600 border-yellow-500',
      indigo: 'bg-indigo-600 border-indigo-500',
      teal: 'bg-teal-600 border-teal-500',
      emerald: 'bg-emerald-600 border-emerald-500',
      gray: 'bg-gray-600 border-gray-500'
    };
    return colorMap[color] || 'bg-gray-600 border-gray-500';
  };
  
  // Legacy categories array for backward compatibility (maps to custom categories)
  const categories = customMeetingCategories.map(c => c.name);

  // Deal pipeline stages
  const pipelineStages = [
    { id: 'tbd', name: 'TBD', color: 'bg-blue-600' },
    { id: 'upcoming', name: 'Upcoming', color: 'bg-yellow-600' },
    { id: 'in_progress', name: 'In Progress', color: 'bg-orange-600' },
    { id: 'done', name: 'Done', color: 'bg-green-600' }
  ];

  // Auto-calculate pipeline stage based on meeting date
  const calculatePipelineStage = (meeting) => {
    const meetingDate = meeting.date;
    
    // If no date or TBD, return 'tbd'
    if (!meetingDate || meetingDate === 'TBD' || meetingDate === '-' || meetingDate === '') {
      return 'tbd';
    }
    
    // Parse the date using local noon to avoid timezone day-shift
    const meetingDateObj = parseLocalDate(meetingDate);
    
    // Check if date is valid
    if (!meetingDateObj) {
      return 'tbd';
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    meetingDateObj.setHours(0, 0, 0, 0);
    
    // Compare dates
    if (meetingDateObj.getTime() === today.getTime()) {
      return 'in_progress'; // Meeting is today
    } else if (meetingDateObj > today) {
      return 'upcoming'; // Meeting is in the future
    } else {
      return 'done'; // Meeting date has passed
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, meeting) => {
    setDraggedMeeting(meeting);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // Show copy effect for conversation drops, move effect for card moves
    e.dataTransfer.dropEffect = draggedConversation ? 'copy' : 'move';
    
    // Add visual indicator for conversation drops
    if (draggedConversation) {
      e.currentTarget.classList.add('ring-2', 'ring-cyan-400', 'bg-cyan-900/20');
    }
  };
  
  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('ring-2', 'ring-cyan-400', 'bg-cyan-900/20');
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    // Handle conversation drop - create new meeting/project from conversation
    if (draggedConversation) {
      console.log(`📥 Dropping conversation "${draggedConversation.title}" into ${targetStage}`);
      
      const category = (draggedConversation.category || 'Meetings').toLowerCase();
      const isProject = category === 'projects';
      const now = new Date();
      
      // Create new meeting/project from conversation
      const newItem = {
        id: `conv_${draggedConversation.id}_${Date.now()}`,
        title: draggedConversation.title || 'Untitled',
        organization: draggedConversation.title || 'Untitled',
        type: isProject ? 'project' : 'meeting',
        date: draggedConversation.date || draggedConversation.created_at || now.toISOString(),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        duration: draggedConversation.duration || 60,
        pipeline_stage: targetStage,
        status: targetStage,
        notes: draggedConversation.summary || '',
        agenda: draggedConversation.transcript ? draggedConversation.transcript.substring(0, 500) : '',
        transcript: draggedConversation.transcript || '',
        transcription_id: draggedConversation.id,
        category: isProject ? 'Project' : 'Meeting',
        meeting_link: '',
        location: '',
        meeting_type: 'virtual',
        documents: [],
        created_from_conversation: true,
        conversation_id: draggedConversation.id
      };
      
      // Save to backend
      try {
        const response = await fetch(`${apiUrl}/api/meetings/?user_id=${encodeURIComponent(userId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newItem)
        });
        
        if (response.ok) {
          const savedItem = await response.json();
          console.log(`✅ Created ${isProject ? 'project' : 'meeting'} from conversation:`, savedItem);
          
          // Add to local state
          const itemWithId = { ...newItem, id: savedItem.id || newItem.id };
          setMeetings(prev => [...prev, itemWithId]);
          
          // Update pipeline stage
          const stagesMap = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
          stagesMap[itemWithId.id] = targetStage;
          localStorage.setItem('meeting_pipeline_stages', JSON.stringify(stagesMap));
          
          toast.success(`Created ${isProject ? 'project' : 'meeting'} "${newItem.title}" in ${targetStage}!`);
        } else {
          throw new Error('Failed to save');
        }
      } catch (error) {
        console.error('Failed to create from conversation:', error);
        // Still add to local state as fallback
        setMeetings(prev => [...prev, newItem]);
        toast.success(`Created ${isProject ? 'project' : 'meeting'} "${newItem.title}" (local only)`);
      }
      
      setDraggedConversation(null);
      return;
    }
    
    if (!draggedMeeting) return;

    // Update meeting pipeline stage in local state immediately (optimistic)
    const updatedMeetings = meetings.map(m =>
      m.id === draggedMeeting.id
        ? { ...m, pipeline_stage: targetStage }
        : m
    );

    setMeetings(updatedMeetings);
    setDraggedMeeting(null);

    // Update meetingStatuses state
    const updatedStatuses = { ...meetingStatuses, [draggedMeeting.id]: targetStage };
    setMeetingStatuses(updatedStatuses);
    localStorage.setItem('meeting_pipeline_stages', JSON.stringify(
      updatedMeetings.reduce((acc, m) => { acc[m.id] = m.pipeline_stage || 'tbd'; return acc; }, {})
    ));

    // ✅ PERSIST to backend — this is the critical fix for cross-device/refresh persistence
    // Skip for calendar-sourced meetings — they aren't in the meetings collection.
    if (typeof draggedMeeting.id === 'string' && draggedMeeting.id.startsWith('cal:')) {
      console.log(`📅 Calendar meeting "${draggedMeeting.title}" moved locally only (no backend persist).`);
    } else {
      try {
        const res = await fetch(
          `${apiUrl}/api/meetings/${draggedMeeting.id}/stage?user_id=${encodeURIComponent(userId)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pipeline_stage: targetStage }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        console.log(`✅ Persisted: ${draggedMeeting.title} → ${targetStage}`);
      } catch (err) {
        console.error('Failed to persist stage to backend:', err);
        toast.error('Could not save position — will retry on next edit');
      }
    }

    console.log(`✅ Moved ${draggedMeeting.title} to ${targetStage}`);
  };
  
  const saveExcelPipelineStages = async (meetingsData) => {
    try {
      // Send updated pipeline stages to backend
      await fetch(`${apiUrl}/api/excel/save-pipeline-stages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stages: meetingsData.reduce((acc, m) => {
            acc[m.id] = m.pipeline_stage || 'tbd';
            return acc;
          }, {})
        })
      });
      console.log('✅ Pipeline stages saved to backend');
    } catch (error) {
      console.error('Failed to save pipeline stages to backend:', error);
      // Continue anyway - localStorage is the backup
    }
  };

  // Update meeting status (upcoming, in_progress, done)
  const updateMeetingStatus = async (meetingId, newStatus) => {
    const updatedStatuses = {
      ...meetingStatuses,
      [meetingId]: newStatus
    };
    setMeetingStatuses(updatedStatuses);
    localStorage.setItem('meeting_statuses', JSON.stringify(updatedStatuses));
    
    // Also save to backend for persistence
    try {
      await fetch(`${apiUrl}/api/excel/save-meeting-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: meetingId,
          status: newStatus
        })
      });
      console.log(`✅ Meeting status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to save meeting status to backend:', error);
    }
  };

  // Get meeting status with fallback to calculated status
  const getMeetingDisplayStatus = (meeting) => {
    // First check if there's a manually set status
    if (meetingStatuses[meeting.id]) {
      return meetingStatuses[meeting.id];
    }
    // Fall back to auto-calculated pipeline stage based on date
    return calculatePipelineStage(meeting);
  };

  const saveExcelCardOrder = async (cardOrder) => {
    try {
      // Send updated card order to backend
      await fetch(`${apiUrl}/api/excel/save-card-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          card_order: cardOrder
        })
      });
      console.log('✅ Card order saved to backend');
    } catch (error) {
      console.error('Failed to save card order to backend:', error);
      // Continue anyway - localStorage is the backup
    }
  };

  const handleDragEnd = () => {
    setDraggedMeeting(null);
  };
  const transcriptCategories = ['All', 'Meetings', 'Projects'];

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    meeting_link: '',
    location: '',
    meeting_type: 'virtual',
    category: 'Real Estate',
    notes: '',
    agenda: '',
    transcript: '',
    transcription_id: '',
    participants: [],
    documents: [],
    pipeline_stage: 'tbd',
    priority: 'medium',
    type: 'project' // 'project' or 'meeting'
  });
  const [formDocuments, setFormDocuments] = useState([]);
  const [uploadingFormDoc, setUploadingFormDoc] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    image_url: '',
    image_file: null
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  // Load project categories from database
  useEffect(() => {
    if (!userId) return;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/project-categories/?user_id=${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.categories?.length) {
            setCustomMeetingCategories(data.categories);
          }
        }
      } catch (err) {
        console.error('Failed to load project categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, [userId]);

  useEffect(() => {
    // Use demo data if in demo mode
    if (isDemoMode && (demoData?.projects || demoData?.calendar_events)) {
      const demoMeetings = [];
      
      // Add projects as meetings
      if (demoData?.projects) {
        demoData.projects.forEach((p, idx) => {
          demoMeetings.push({
            id: p.id,
            title: p.name,
            organization: 'Demo Project',
            type: 'project',
            pipeline_stage: p.status === 'Active' ? 'in_progress' : 'prospecting',
            progress: p.progress || 0,
            deadline: p.deadline,
            category: 'projects',
            participants: []
          });
        });
      }
      
      // Add calendar events as meetings
      if (demoData?.calendar_events) {
        demoData.calendar_events.filter(e => e.type === 'meeting').forEach(evt => {
          demoMeetings.push({
            id: evt.id,
            title: evt.title,
            organization: evt.client || evt.location || '',
            type: 'meeting',
            date: evt.start_time?.split('T')[0],
            time: new Date(evt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
            pipeline_stage: 'prospecting',
            participants: evt.client ? [{ name: evt.client, company: '' }] : []
          });
        });
      }
      
      setMeetings(demoMeetings);
      setLoading(false);
      console.log(`📅 Demo mode: Loaded ${demoMeetings.length} demo meetings/projects`);
      return;
    }
    
    // Default to Excel - always try to load Excel data first
    const loadExcelData = async () => {
      try {
        // First try the new excel_meetings collection
        const response = await apiCall('/excel/get-desktop-data');
        if (response.meetings && response.meetings.length > 0) {
          setExcelMeetings(response.meetings);
          
          const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
          const meetingsWithStages = response.meetings.map(m => ({
            ...m,
            pipeline_stage: savedStages[m.id] || m.pipeline_stage || 'prospecting'
          }));
          setMeetings(meetingsWithStages);
          console.log('✅ Loaded Excel data from backend (excel_meetings collection)');
          
          // Also load saved card order from backend
          try {
            const cardOrderResponse = await apiCall('/excel/get-card-order');
            if (cardOrderResponse.card_order && Object.keys(cardOrderResponse.card_order).length > 0) {
              localStorage.setItem('kanban_card_order', JSON.stringify(cardOrderResponse.card_order));
              console.log('✅ Loaded card order from backend');
            }
          } catch (err) {
            console.log('No saved card order found in backend');
          }
          
          // Load saved meeting statuses from backend
          try {
            const statusesResponse = await apiCall('/excel/get-meeting-statuses');
            if (statusesResponse.statuses && Object.keys(statusesResponse.statuses).length > 0) {
              setMeetingStatuses(statusesResponse.statuses);
              localStorage.setItem('meeting_statuses', JSON.stringify(statusesResponse.statuses));
              console.log('✅ Loaded meeting statuses from backend');
            }
          } catch (err) {
            console.log('No saved meeting statuses found in backend');
          }
        } else {
          // Fallback: Try the meetings collection (for data synced with old endpoint)
          console.log('No data in excel_meetings, checking meetings collection...');
          try {
            const meetingsResponse = await apiCall('/meetings/');
            if (Array.isArray(meetingsResponse) && meetingsResponse.length > 0) {
              // Filter for Excel-sourced meetings
              const excelMeetings = meetingsResponse.filter(m => 
                m.source === 'desktop_excel' || 
                m.source === 'desktop_excel_mac' ||
                (m.source && m.source.includes('excel'))
              );
              
              if (excelMeetings.length > 0) {
                // Transform to expected format
                const transformedMeetings = excelMeetings.map(m => ({
                  ...m,
                  id: m.id || m._id,
                  title: m.title || `Meeting with ${m.participants?.[0] || 'Contact'}`,
                  organization: m.company || m.organization || m.location || '',
                  pipeline_stage: m.pipeline_stage || 'prospecting',
                  participants: m.participants ? 
                    (Array.isArray(m.participants) && typeof m.participants[0] === 'string' 
                      ? m.participants.map(p => ({ name: p, company: m.company || '' }))
                      : m.participants)
                    : []
                }));
                
                setExcelMeetings(transformedMeetings);
                setMeetings(transformedMeetings);
                console.log(`✅ Loaded ${transformedMeetings.length} Excel meetings from meetings collection`);
                return;
              }
            }
          } catch (err) {
            console.log('Could not load from meetings collection:', err);
          }
          
          // Try localStorage as last resort
          console.log('No Excel data from backend, checking localStorage...');
          const savedExcelData = localStorage.getItem('excel_meetings');
          if (savedExcelData) {
            const excelData = JSON.parse(savedExcelData);
            setExcelMeetings(excelData);
            
            const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
            const meetingsWithStages = excelData.map(m => ({
              ...m,
              pipeline_stage: savedStages[m.id] || 'prospecting'
            }));
            setMeetings(meetingsWithStages);
            console.log('✅ Loaded Excel data from localStorage');
          } else {
            // No Excel data at all - show empty with message
            setMeetings([]);
            console.log('No Excel data found - page will show empty');
          }
        }
      } catch (error) {
        console.error('Error loading Excel data:', error);
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Load personal meetings from backend API
    const loadPersonalMeetings = async () => {
      // Don't load if no user
      if (!userId) {
        setLoading(false);
        setPersonalMeetings([]);
        setMeetings([]);
        return;
      }
      
      try {
        setLoading(true);
        console.log('📅 Loading personal meetings for user:', userId);
        const response = await apiCall(`/meetings/?user_id=${encodeURIComponent(userId)}`);
        if (response && Array.isArray(response) && response.length > 0) {
          // Transform backend meetings to match UI format
          const transformedMeetings = response.map((meeting, index) => ({
            id: meeting._id || meeting.id || `personal-${index}`,
            title: meeting.title || 'Untitled Project',
            organization: meeting.organization || meeting.title || 'Untitled Project',
            date: meeting.date || new Date().toISOString().split('T')[0],
            time: meeting.time || '09:00',
            duration: meeting.duration || '1 hour',
            participants: meeting.participants || [],
            status: meeting.status || 'scheduled',
            category: meeting.category || 'Personal',
            type: meeting.type || 'personal',
            location: meeting.location || '',
            notes: meeting.notes || '',
            agenda: meeting.agenda || '',
            pipeline_stage: meeting.pipeline_stage || 'tbd',
            priority: meeting.priority || 'medium',
            created_at: meeting.created_at || new Date().toISOString(),
            panel_notes: meeting.panel_notes || null,
            documents: meeting.documents || [],
            transcription_id: meeting.transcription_id || null,
            transcription_summary: meeting.transcription_summary || null,
            transcript: meeting.transcript || null,
          }));
          setPersonalMeetings(transformedMeetings);
          setMeetings(transformedMeetings);
          // Restore pipeline stage from DB so it persists across deploys
          const savedStages = {};
          const savedTranscriptionLinks = {};
          transformedMeetings.forEach(m => {
            if (m.pipeline_stage && m.pipeline_stage !== 'prospecting') {
              savedStages[m.id] = m.pipeline_stage;
            }
            if (m.transcription_id) {
              savedTranscriptionLinks[m.id] = m.transcription_id;
            }
          });
          if (Object.keys(savedStages).length > 0) {
            setMeetingStatuses(prev => ({ ...prev, ...savedStages }));
          }
          if (Object.keys(savedTranscriptionLinks).length > 0) {
            setMeetingTranscriptions(prev => ({ ...prev, ...savedTranscriptionLinks }));
          }
          console.log(`✅ Loaded ${transformedMeetings.length} personal meetings`);
        } else {
          setPersonalMeetings([]);
          setMeetings([]);
        }
      } catch (error) {
        console.error('Error loading personal meetings:', error);
        setPersonalMeetings([]);
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };

    // Load calendar events tagged with category "meeting" so they appear on
    // this page alongside CRM-created meetings (one source of truth).
    const loadCalendarMeetings = async () => {
      if (!userId) return;
      try {
        const res = await fetch(
          `${apiUrl}/api/outlook-calendar/events-by-category?user_id=${encodeURIComponent(userId)}&category=meeting&days_back=60&days_forward=365`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!data || !Array.isArray(data.events) || data.events.length === 0) return;

        const transformed = data.events.map(ev => {
          const startIso = ev.start || '';
          const d = startIso ? new Date(startIso) : null;
          const pad = (n) => String(n).padStart(2, '0');
          const dateStr = d && !isNaN(d.getTime())
            ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
            : new Date().toISOString().split('T')[0];
          const timeStr = d && !isNaN(d.getTime()) && !ev.is_all_day
            ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
            : '09:00';
          // Mark id with a prefix so it can't collide with CRM meeting ids
          return {
            id: `cal:${ev.id}`,
            title: ev.title || 'Calendar Meeting',
            organization: ev.organizer || '',
            date: dateStr,
            time: timeStr,
            duration: '1 hour',
            participants: [],
            status: 'scheduled',
            category: 'Meetings',
            type: 'calendar',
            location: ev.location || '',
            notes: '',
            agenda: '',
            pipeline_stage: 'tbd',
            priority: 'medium',
            created_at: ev.start || new Date().toISOString(),
            online_meeting_url: ev.online_meeting_url || null,
            web_link: ev.web_link || null,
            source: 'calendar',     // flag for UI: hide destructive actions
            read_only: true,
            calendar_event_id: ev.id,
          };
        });

        // Merge into existing meetings, de-duping by id
        setPersonalMeetings(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const additions = transformed.filter(m => !existingIds.has(m.id));
          return [...prev, ...additions];
        });
        setMeetings(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const additions = transformed.filter(m => !existingIds.has(m.id));
          return [...prev, ...additions];
        });
        console.log(`📅 Merged ${transformed.length} calendar meetings into Meetings page`);
      } catch (err) {
        console.warn('Could not load calendar meetings:', err);
      }
    };

    // Set Personal as default immediately (before data loads)
    setDataSource('personal');
    localStorage.setItem('data_source', 'personal');
    
    // Load personal meetings by default
    loadPersonalMeetings();
    loadConversations();
    // Also pull in any Calendar events tagged with the "meeting" category
    // (fire-and-forget; runs after personal meetings hydrate)
    setTimeout(() => { loadCalendarMeetings(); }, 100);
    
    // Load transcription links from meeting data (backend-persisted)
    // No longer using localStorage for transcription links
    
    // Check for URL query parameters (from Text Center)
    const searchParams = new URLSearchParams(window.location.search);
    const prefillParam = searchParams.get('prefill');
    
    if (prefillParam) {
      try {
        const prefillData = JSON.parse(decodeURIComponent(prefillParam));
        setFormData(prev => ({
          ...prev,
          title: prefillData.title || prev.title,
          date: prefillData.date || prev.date,
          time: prefillData.time || prev.time,
          duration: prefillData.duration || prev.duration,
          location: prefillData.location || prev.location,
          notes: prefillData.notes || prev.notes,
          transcript: prefillData.transcript || prev.transcript,
          participants: prefillData.participants || prev.participants
        }));
        setIsCreating(true);
        // Clear URL params
        window.history.replaceState({}, '', '/meetings');
      } catch (error) {
        console.error('Error parsing prefill data:', error);
      }
    }
    
    // Check for prefill data from navigation state (from Transcribe or Email Center)
    if (location.state?.prefillData) {
      const { title, date, time, notes, transcript, transcription_id, participants } = location.state.prefillData;
      setFormData(prev => ({
        ...prev,
        title: title || prev.title,
        date: date || prev.date,
        time: time || prev.time,
        notes: notes || prev.notes,
        transcript: transcript || prev.transcript,
        transcription_id: transcription_id || prev.transcription_id,
        participants: participants || prev.participants
      }));
      setIsCreating(true);
      window.history.replaceState({}, document.title);
    }
    
    // Handle navigation from dashboard widget - set mainTab
    if (location.state?.mainTab) {
      setMainTab(location.state.mainTab);
    }
    
    // Handle navigation from dashboard widget - select specific meeting/project
    if (location.state?.selectedMeetingId || location.state?.selectedProjectId) {
      const itemId = location.state.selectedMeetingId || location.state.selectedProjectId;
      // We'll set the selected meeting after meetings are loaded
      // Store the ID in a ref or state to be used after data loads
      setTimeout(() => {
        // Find the meeting/project from the loaded data
        const allItems = [...personalMeetings, ...smartsheetMeetings, ...excelMeetings];
        const foundItem = allItems.find(m => m.id === itemId);
        if (foundItem) {
          setSelectedMeeting(foundItem);
        }
      }, 500);
      window.history.replaceState({}, document.title);
    }
  }, [location, userId, isDemoMode, demoData]);

  const loadMeetings = async () => {
    // Don't load if no user
    if (!userId) {
      setLoading(false);
      setSmartsheetMeetings([]);
      return;
    }
    
    try {
      // Load from Smartsheet with user_id
      console.log('📅 Loading meetings from Smartsheet for user:', userId);
      
      // Use the API service to ensure correct URL handling
      const data = await apiCall(`/smartsheet/meetings?user_id=${encodeURIComponent(userId)}`);
      
      if (data.success && data.meetings) {
        console.log(`✅ Loaded ${data.count} meetings from Smartsheet`);
        
        // Transform Smartsheet data to match our UI format
        const transformedMeetings = data.meetings.map((meeting, index) => {
          // Parse date from Smartsheet (format: "1/30/23  10:00 a.m." or other formats)
          let meetingDate = meeting.cells['Date of Meeting / Scheduled'];
          let meetingTime = '10:00';
          let meetingHour = 10;
          
          if (meetingDate) {
            try {
              // Handle format like "1/30/23  10:00 a.m."
              if (typeof meetingDate === 'string') {
                // Split date and time
                const parts = meetingDate.trim().split(/\s+/);
                const datePart = parts[0]; // "1/30/23"
                const timePart = parts[1] || '10:00'; // "10:00"
                const ampm = parts[2] || 'a.m.'; // "a.m."
                
                // Parse date: "1/30/23" -> "2023-01-30"
                const [month, day, year] = datePart.split('/');
                const fullYear = year.length === 2 ? `20${year}` : year;
                meetingDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                
                // Parse time and hour for status calculation
                const timeMatch = timePart.match(/(\d+):(\d+)/);
                if (timeMatch) {
                  meetingHour = parseInt(timeMatch[1]);
                  const minutes = timeMatch[2];
                  
                  // Convert to 24-hour format
                  if (ampm.toLowerCase().includes('p.m.') && meetingHour !== 12) {
                    meetingHour += 12;
                  } else if (ampm.toLowerCase().includes('a.m.') && meetingHour === 12) {
                    meetingHour = 0;
                  }
                  
                  meetingTime = `${meetingHour.toString().padStart(2, '0')}:${minutes}`;
                }
              } else if (typeof meetingDate === 'number') {
                // If it's an Excel serial number
                const excelEpoch = new Date(1900, 0, 1);
                const date = new Date(excelEpoch.getTime() + (meetingDate - 2) * 24 * 60 * 60 * 1000);
                meetingDate = date.toISOString().split('T')[0];
              }
            } catch (e) {
              console.error('Error parsing date:', e);
              meetingDate = new Date().toISOString().split('T')[0];
            }
          } else {
            meetingDate = new Date().toISOString().split('T')[0];
          }
          
          // Calculate meeting status based on date and time
          const calculateStatus = () => {
            const now = new Date();
            const meetingDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
            const meetingEndTime = new Date(meetingDateTime.getTime() + (2 * 60 * 60 * 1000)); // 2 hours later
            
            if (now < meetingDateTime) {
              return 'upcoming';
            } else if (now >= meetingDateTime && now < meetingEndTime) {
              return 'in_progress';
            } else {
              return 'done';
            }
          };
          
          const contactName = meeting.cells['Contact Name'] || 'No participant';
          const organization = meeting.cells['Organization'] || '';
          const email = meeting.cells['Email'] || '';
          const meetingType = meeting.cells['Type of Meeting - In Person / Virtual'];
          const isVirtual = meetingType?.toLowerCase().includes('zoom') || 
                           meetingType?.toLowerCase().includes('virtual') ||
                           meetingType?.toLowerCase().includes('teams');
          
          // Generate avatar from name using UI Avatars service
          let imageUrl = null;
          if (contactName && contactName !== 'No participant') {
            imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`;
          }
          
          // Build participant object with image URL
          const participant = contactName !== 'No participant' ? {
            name: contactName,
            company: organization,
            email: email,
            image_url: imageUrl
          } : null;
          
          // Debug: Log first few meetings
          if (index < 3) {
            console.log(`Meeting ${index}:`, {
              contactName,
              email,
              org: organization,
              date: meetingDate,
              time: meetingTime,
              status: calculateStatus(),
              rawDate: meeting.cells['Date of Meeting / Scheduled']
            });
          }
          
          return {
            id: meeting.smartsheet_row_id,
            smartsheet_row_id: meeting.smartsheet_row_id,
            row_number: meeting.row_number,
            title: contactName,
            contact_name: contactName,
            email: email,
            organization: organization,
            date: meetingDate,
            time: meetingTime,
            duration: 120, // 2 hours
            meeting_type: isVirtual ? 'virtual' : 'in-person',
            location: meetingType || 'TBD',
            category: 'Investments',
            status: calculateStatus(),
            participants: participant ? [participant] : [],
            notes: '',
            meeting_link: isVirtual ? meetingType : '',
            agenda: `Meeting with ${contactName}${organization ? ` from ${organization}` : ''}`,
            from_smartsheet: true
          };
        });
        
        setSmartsheetMeetings(transformedMeetings);
        
        // Load pipeline stages from localStorage
        const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
        const meetingsWithStages = transformedMeetings.map(m => ({
          ...m,
          pipeline_stage: savedStages[m.id] || 'prospecting'
        }));
        
        setMeetings(meetingsWithStages);
        setLastSync(new Date());
      } else {
        console.error('Failed to load Smartsheet meetings:', data);
        // Show empty state instead of mock data
        console.log('📅 No Smartsheet data available');
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error loading meetings:', error);
      // Show empty state instead of mock data
      console.log('📅 No meetings data available');
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingExcel(true);
    setExcelFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('📊 Excel data parsed:', jsonData.length, 'rows');
        
        // Transform Excel data to meeting format
        const transformedMeetings = jsonData.map((row, index) => {
          // Parse date from Excel (format: "1/15/2025" or Excel serial number)
          let meetingDate = row['Meeting Date'] || row['Date'];
          let meetingTime = row['Meeting Time (CST)'] || row['Meeting Time'] || '10:00';
          
          // Handle Excel date serial numbers
          if (typeof meetingDate === 'number') {
            const excelEpoch = new Date(1900, 0, 1);
            const date = new Date(excelEpoch.getTime() + (meetingDate - 2) * 24 * 60 * 60 * 1000);
            meetingDate = date.toISOString().split('T')[0];
          } else if (typeof meetingDate === 'string') {
            // Parse MM/DD/YYYY format
            const parts = meetingDate.split('/');
            if (parts.length === 3) {
              const [month, day, year] = parts;
              meetingDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }
          
          // Parse time (format: "10:00 AM CST" or "10:00")
          if (typeof meetingTime === 'string') {
            const timeMatch = meetingTime.match(/(\d+):(\d+)/);
            if (timeMatch) {
              let hour = parseInt(timeMatch[1]);
              const minutes = timeMatch[2];
              
              // Convert to 24-hour format if AM/PM present
              if (meetingTime.includes('PM') && hour !== 12) {
                hour += 12;
              } else if (meetingTime.includes('AM') && hour === 12) {
                hour = 0;
              }
              
              meetingTime = `${hour.toString().padStart(2, '0')}:${minutes}`;
            }
          }
          
          const contactName = row['Contact Name'] || 'No participant';
          const company = row['Company'] || '';
          const email = row['Email'] || '';
          const phone = row['Phone'] || '';
          const status = row['Status'] || 'Scheduled';
          const meetingType = row['Meeting Type'] || 'Virtual';
          const notes = row['Additional Notes'] || '';
          
          // Determine if virtual based on meeting type
          const isVirtual = meetingType.toLowerCase().includes('zoom') || 
                           meetingType.toLowerCase().includes('teams') ||
                           meetingType.toLowerCase().includes('virtual');
          
          // Generate avatar from name
          let imageUrl = null;
          if (contactName && contactName !== 'No participant') {
            imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`;
          }
          
          // Build participant object
          const participant = contactName !== 'No participant' ? {
            name: contactName,
            company: company,
            email: email,
            phone: phone,
            image_url: imageUrl
          } : null;
          
          // Calculate meeting status
          const calculateStatus = () => {
            const now = new Date();
            const meetingDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
            const meetingEndTime = new Date(meetingDateTime.getTime() + (2 * 60 * 60 * 1000));
            
            if (now < meetingDateTime) {
              return 'upcoming';
            } else if (now >= meetingDateTime && now < meetingEndTime) {
              return 'in_progress';
            } else {
              return 'done';
            }
          };
          
          return {
            id: `excel-${index}-${Date.now()}`,
            title: `Meeting with ${contactName}`,
            date: meetingDate,
            time: meetingTime,
            duration: 60,
            meeting_type: isVirtual ? 'virtual' : 'in-person',
            category: 'Investments',
            notes: notes,
            agenda: notes || `Meeting scheduled with ${contactName} from ${company}`,
            organization: company,
            location: isVirtual ? 'Virtual' : company,
            status: calculateStatus(),
            excel_status: status,
            participants: participant ? [participant] : [],
            pipeline_stage: 'prospecting'
          };
        });
        
        console.log('✅ Transformed', transformedMeetings.length, 'Excel meetings');
        setExcelMeetings(transformedMeetings);
        
        // Save to localStorage
        localStorage.setItem('excel_meetings', JSON.stringify(transformedMeetings));
        localStorage.setItem('excel_file_name', file.name);
        localStorage.setItem('excel_upload_date', new Date().toISOString());
        localStorage.setItem('data_source', 'excel'); // Save data source preference
        
        // Switch to Excel data source
        setDataSource('excel');
        
        // Apply saved pipeline stages
        const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
        const meetingsWithStages = transformedMeetings.map(m => ({
          ...m,
          pipeline_stage: savedStages[m.id] || 'prospecting'
        }));
        
        setMeetings(meetingsWithStages);
        
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        alert('Error parsing Excel file. Please check the file format.');
      } finally {
        setUploadingExcel(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const syncWithSmartsheet = async () => {
    setSyncing(true);
    try {
      console.log('🔄 Syncing with Smartsheet...');
      await loadMeetings();
      console.log('✅ Sync complete');
    } catch (error) {
      console.error('❌ Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const loadConversations = async () => {
    // Don't load if no user
    if (!userId) {
      setConversations([]);
      return;
    }
    
    try {
      // Load from BOTH conversations-transcripts AND fireflies endpoints
      console.log('📋 Loading conversations for user:', userId);
      
      // Fetch from both sources in parallel
      const [conversationsResponse, firefliesResponse] = await Promise.all([
        apiCall(`/conversations-transcripts/?user_id=${encodeURIComponent(userId)}&limit=500`).catch(() => null),
        apiCall(`/fireflies/transcripts?user_id=${encodeURIComponent(userId)}&limit=500`).catch(() => null)
      ]);
      
      // Handle conversations-transcripts response
      let conversationsArray = [];
      if (conversationsResponse && Array.isArray(conversationsResponse)) {
        conversationsArray = conversationsResponse;
      } else if (conversationsResponse && conversationsResponse.conversations && Array.isArray(conversationsResponse.conversations)) {
        conversationsArray = conversationsResponse.conversations;
      }
      
      // Handle fireflies response (it returns {items: [...], total: N})
      let firefliesArray = [];
      if (firefliesResponse && firefliesResponse.items && Array.isArray(firefliesResponse.items)) {
        // Transform Fireflies format to match conversations format
        firefliesArray = firefliesResponse.items.map(t => ({
          ...t,
          id: t.id || t.fireflies_id,
          title: t.title || t.meeting_title,
          source: 'fireflies',
          fireflies_id: t.fireflies_id || t.id,
          ai_breakdown: t.ai_breakdown,
          attendees: t.attendees || t.participants || [],
          date: t.date || t.meeting_date || t.created_at,
          created_at: t.created_at || t.date
        }));
      } else if (firefliesResponse && Array.isArray(firefliesResponse)) {
        firefliesArray = firefliesResponse.map(t => ({
          ...t,
          id: t.id || t.fireflies_id,
          title: t.title || t.meeting_title,
          source: 'fireflies',
          fireflies_id: t.fireflies_id || t.id,
          ai_breakdown: t.ai_breakdown,
          attendees: t.attendees || t.participants || [],
          date: t.date || t.meeting_date || t.created_at,
          created_at: t.created_at || t.date
        }));
      }
      
      // Merge both arrays, avoiding duplicates by ID
      const existingIds = new Set(conversationsArray.map(c => c.id));
      const mergedArray = [
        ...conversationsArray,
        ...firefliesArray.filter(f => !existingIds.has(f.id) && !existingIds.has(f.fireflies_id))
      ];
      
      console.log(`📋 Loaded ${conversationsArray.length} from conversations-transcripts, ${firefliesArray.length} from fireflies, ${mergedArray.length} total`);
      
      if (mergedArray.length > 0) {
        // Sort by created_at descending (newest first)
        const sortedConversations = mergedArray.sort((a, b) => {
          const dateA = new Date(a.created_at || a.date || 0);
          const dateB = new Date(b.created_at || b.date || 0);
          return dateB - dateA; // Newest first
        });
        
        setConversations(sortedConversations);
        console.log(`✅ Loaded ${sortedConversations.length} total conversations for user ${userId}`);
      } else {
        // Show empty state instead of mock data
        console.log('📋 No conversations found for user');
        setConversations([]);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      // Show empty state instead of mock data
      setConversations([]);
    }
  };

  // Delete a conversation
  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) {
      return;
    }
    
    try {
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${apiUrl}/api/conversations-transcripts/${conversationId}?user_id=${encodeURIComponent(userId)}`, true);
        xhr.timeout = 30000;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true });
          } else {
            reject(new Error(`Delete failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      setExpandedConversation(null);
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  // Update a conversation
  const updateConversation = async (conversationId, updates) => {
    try {
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${apiUrl}/api/conversations-transcripts/${conversationId}?user_id=${encodeURIComponent(userId)}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 30000;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              resolve({ success: true });
            }
          } else {
            reject(new Error(`Update failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(JSON.stringify(updates));
      });
      
      // Update local state
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, ...updates } : c
      ));
      
      // Update expanded conversation if it's the one being edited
      if (expandedConversation && expandedConversation.id === conversationId) {
        setExpandedConversation(prev => ({ ...prev, ...updates }));
      }
      
      setConversationEditMode(false);
      toast.success('Conversation updated');
    } catch (error) {
      console.error('Error updating conversation:', error);
      toast.error('Failed to update conversation');
    }
  };

  // Load AI breakdown for a transcription
  const loadBreakdownForTranscript = async (transcript) => {
    if (!transcript || loadedBreakdowns[transcript.id] || loadingBreakdowns[transcript.id]) return;
    
    // If breakdown already exists in the transcript object, cache it
    if (transcript.ai_breakdown) {
      setLoadedBreakdowns(prev => ({ ...prev, [transcript.id]: transcript.ai_breakdown }));
      return;
    }
    
    setLoadingBreakdowns(prev => ({ ...prev, [transcript.id]: true }));
    
    try {
      // Try Fireflies endpoint first (most transcriptions are from Fireflies)
      // Note: Backend route is POST-only
      const ffId = transcript.fireflies_id || transcript.id;
      const response = await apiCall(`/fireflies/transcripts/${ffId}/breakdown?user_id=${userId}`, { method: 'POST' });
      
      if (response && response.breakdown) {
        setLoadedBreakdowns(prev => ({ ...prev, [transcript.id]: response.breakdown }));
        // Also update the transcript in conversations state
        setConversations(prev => prev.map(c => 
          c.id === transcript.id ? { ...c, ai_breakdown: response.breakdown } : c
        ));
      }
    } catch (error) {
      console.log('Could not load breakdown for transcript:', transcript.id, error);
    } finally {
      setLoadingBreakdowns(prev => ({ ...prev, [transcript.id]: false }));
    }
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      console.log('🗑️ Deleting conversation:', conversationId);
      
      const response = await apiCall(`/conversations-transcripts/${conversationId}`, {
        method: 'DELETE'
      });
      
      if (response) {
        // Remove from local state
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        
        // Remove any meeting links to this conversation
        const updatedLinks = { ...meetingTranscriptions };
        Object.keys(updatedLinks).forEach(key => {
          if (updatedLinks[key] === conversationId) {
            delete updatedLinks[key];
            // Clear transcription_id in backend
            apiCall(`/meetings/${key}`, {
              method: 'PUT',
              body: JSON.stringify({ transcription_id: null })
            }).catch(err => console.error('Failed to clear transcription link:', err));
          }
        });
        setMeetingTranscriptions(updatedLinks);
        
        console.log('✅ Conversation deleted successfully');
      }
    } catch (error) {
      console.error('❌ Error deleting conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  // Timer for recording
  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  // ─── GOOGLE SHEETS FUNCTIONS ─────────────────────────────────────────────────
  
  // Check Google Sheets connection status on load
  useEffect(() => {
    if (!userId) return;
    const checkSheetsConnection = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/google-sheets/status?user_id=${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          setSheetsConnected(data.connected);
          if (data.connected) {
            loadSheetConnections();
          }
        }
      } catch (err) {
        console.error('Failed to check Google Sheets status:', err);
      }
    };
    checkSheetsConnection();
    
    // Check URL for sheets_connected param
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sheets_connected') === 'true') {
      setSheetsConnected(true);
      toast.success('Google Sheets connected successfully!');
      loadSheetConnections();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (urlParams.get('sheets_error')) {
      toast.error(`Google Sheets error: ${urlParams.get('sheets_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [userId]);
  
  const loadSheetConnections = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/google-sheets/list?user_id=${encodeURIComponent(userId)}`);
      if (res.ok) {
        const data = await res.json();
        setSheetConnections(data.connections || []);
      }
    } catch (err) {
      console.error('Failed to load sheet connections:', err);
    }
  };
  
  const handleConnectGoogleSheets = () => {
    if (!userId) {
      toast.error('Please log in to connect Google Sheets');
      return;
    }
    // Redirect to Google OAuth
    window.location.href = `${apiUrl}/api/google-sheets/auth/login?user_id=${encodeURIComponent(userId)}&redirect_after=/projects-re`;
  };
  
  const handleDisconnectSheets = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/google-sheets/disconnect?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setSheetsConnected(false);
        setSheetConnections([]);
        toast.success('Google Sheets disconnected');
      }
    } catch (err) {
      toast.error('Failed to disconnect Google Sheets');
    }
  };
  
  const handleConnectSpreadsheet = async () => {
    if (!spreadsheetId.trim()) {
      toast.error('Please enter a Spreadsheet ID');
      return;
    }
    
    setSheetsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/google-sheets/connect?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadsheet_id: spreadsheetId.trim(),
          sheet_name: sheetName || 'Sheet1',
          sync_type: 'both'
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Connected to "${data.spreadsheet_title}"`);
        setSpreadsheetId('');
        setShowSheetsModal(false);
        loadSheetConnections();
      } else {
        toast.error(data.detail || 'Failed to connect spreadsheet');
      }
    } catch (err) {
      toast.error('Failed to connect spreadsheet');
    } finally {
      setSheetsLoading(false);
    }
  };
  
  const handleImportFromSheets = async (connection) => {
    setSyncingSheets(true);
    try {
      const res = await fetch(`${apiUrl}/api/google-sheets/sync/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          spreadsheet_id: connection.spreadsheet_id,
          sheet_name: connection.sheet_name || 'Sheet1'
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || `Imported ${data.imported} items`);
        // Refresh meetings list
        loadMeetings();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (err) {
      toast.error('Failed to import from Google Sheets');
    } finally {
      setSyncingSheets(false);
    }
  };
  
  const handleExportToSheets = async (connection) => {
    setSyncingSheets(true);
    try {
      const res = await fetch(`${apiUrl}/api/google-sheets/sync/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          spreadsheet_id: connection.spreadsheet_id,
          sheet_name: connection.sheet_name || 'Sheet1'
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || `Exported ${data.exported} items`);
      } else {
        toast.error(data.detail || 'Export failed');
      }
    } catch (err) {
      toast.error('Failed to export to Google Sheets');
    } finally {
      setSyncingSheets(false);
    }
  };

  // ─── DOCUMENT UPLOAD FUNCTIONS ───────────────────────────────────────────────
  
  const handleDocumentFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['application/pdf', 'text/csv', 'image/jpeg', 'image/png', 'image/webp'];
    const validExtensions = ['.pdf', '.csv', '.jpg', '.jpeg', '.png', '.webp'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      toast.error('Unsupported file type. Please upload PDF, CSV, JPG, PNG, or WebP');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setDocumentUploadFile(file);
    setDocumentUploadProgress('reading');
    
    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      setDocumentUploadProgress('parsing');
      
      // Send to parse endpoint
      const res = await fetch(`${apiUrl}/api/meetings/parse-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_base64: base64,
          file_type: file.type || 'application/octet-stream',
          file_name: file.name,
          user_id: userId
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setParsedMeetingsFromDoc(data.meetings || []);
        toast.success(data.message || `Found ${data.meetings?.length || 0} meetings`);
      } else {
        toast.error(data.detail || 'Failed to parse document');
        setParsedMeetingsFromDoc(null);
      }
    } catch (err) {
      console.error('Document parse error:', err);
      toast.error('Failed to parse document');
      setParsedMeetingsFromDoc(null);
    } finally {
      setDocumentUploadProgress(null);
      if (documentFileInputRef.current) {
        documentFileInputRef.current.value = '';
      }
    }
  };
  
  const handleImportParsedMeetings = async () => {
    if (!parsedMeetingsFromDoc || parsedMeetingsFromDoc.length === 0) return;
    
    setDocumentUploadProgress('importing');
    
    try {
      const res = await fetch(`${apiUrl}/api/meetings/import-parsed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          meetings: parsedMeetingsFromDoc
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message || `Imported ${data.imported} meetings`);
        setShowDocumentUploadModal(false);
        setParsedMeetingsFromDoc(null);
        setDocumentUploadFile(null);
        // Refresh meetings list
        loadMeetings();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (err) {
      toast.error('Failed to import meetings');
    } finally {
      setDocumentUploadProgress(null);
    }
  };
  
  const handleCloseDocumentUploadModal = () => {
    setShowDocumentUploadModal(false);
    setParsedMeetingsFromDoc(null);
    setDocumentUploadFile(null);
    setDocumentUploadProgress(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Create audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioSource.connect(analyser);
      analyserRef.current = analyser;

      // Start visualizing audio levels
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateWaveform = () => {
        if (!analyserRef.current) return;
        
        analyser.getByteFrequencyData(dataArray);
        
        // Sample 12 points across the frequency data for our 12 bars
        const bars = [];
        const samplesPerBar = Math.floor(bufferLength / 12);
        
        for (let i = 0; i < 12; i++) {
          const start = i * samplesPerBar;
          const end = start + samplesPerBar;
          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += dataArray[j];
          }
          const average = sum / samplesPerBar;
          // Normalize to 0-100 range
          bars.push(Math.min(100, (average / 255) * 100));
        }
        
        setAudioLevels(bars);
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };

      updateWaveform();

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        
        // Stop visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContext) {
          audioContext.close();
        }
        analyserRef.current = null;
        setAudioLevels(Array(12).fill(0));
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setTranscriptionError(null);
    } catch (err) {
      console.error('Error starting recording:', err);
      setTranscriptionError('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeRecording = async () => {
    if (!audioBlob) return;

    setTranscribing(true);
    setTranscriptionError(null);

    try {
      // Step 1: Convert audio blob to file and transcribe using Whisper
      // Use XMLHttpRequest to bypass any fetch interceptors
      console.log('🎤 Sending audio for transcription...');
      toast.loading('Transcribing audio...', { id: 'transcribe-toast' });
      
      const transcribeResult = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/whisper/transcribe`, true);
        xhr.timeout = 120000; // 2 minute timeout for transcription
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error('Invalid transcription response'));
            }
          } else {
            reject(new Error(`Transcription failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during transcription'));
        xhr.ontimeout = () => reject(new Error('Transcription timed out'));
        
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        xhr.send(formData);
      });
      
      console.log('✅ Transcription complete:', transcribeResult);
      
      toast.loading('Generating AI summary...', { id: 'transcribe-toast' });
      
      // Step 2: Convert audio blob to base64 for storage
      const reader = new FileReader();
      const audioBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      // Step 3: Save to conversations-transcripts using QUICK SAVE so the
      // upload completes immediately even on production where the 3-step
      // GPT pipeline can stall behind ingress timeouts. AI title + summary
      // generation kicks off server-side in the background. We deliberately
      // do NOT ship `audio_data` here — the base64-encoded webm easily
      // overruns the production ingress body-size limit, causing a 500. The
      // audio stays in the user's browser; only the transcript persists.
      const savePayload = {
        transcript: transcribeResult.text || transcribeResult.transcript || '',
        duration: recordingTime,
        language: transcribeResult.language || 'en',
        user_id: userId
      };

      console.log('💾 Saving conversation (quick save + background AI)...');

      // Use XMLHttpRequest for save as well
      const savedConversation = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/conversations-transcripts/?quick_save=true&user_id=${encodeURIComponent(userId)}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 60000; // 60s — quick save should return in <2s

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error('Invalid save response'));
            }
          } else {
            reject(new Error(`Save failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during save'));
        xhr.ontimeout = () => reject(new Error('Save timed out'));

        xhr.send(JSON.stringify(savePayload));
      });

      console.log('✅ Conversation saved (AI processing in background):', savedConversation);

      // Step 4: Add the new conversation directly to state (at the top) - INSTANT UPDATE
      if (savedConversation && savedConversation.id) {
        const newConversation = {
          ...savedConversation,
          date: savedConversation.date || savedConversation.created_at || new Date().toISOString(),
          category: savedConversation.category || 'Meetings',
          duration: savedConversation.duration || `${Math.floor(recordingTime / 60)}:${(recordingTime % 60).toString().padStart(2, '0')}`
        };
        setConversations(prev => [newConversation, ...prev.filter(c => c.id !== newConversation.id)]);
        console.log('✅ Added new conversation to list instantly');

        // Refetch the conversation a few seconds later to pick up the
        // background-generated AI title + summary. Two attempts cover both
        // fast (~3s) and slow (~10s) LLM responses.
        const refreshAi = async () => {
          try {
            const refreshed = await apiCall(`/conversations-transcripts/?user_id=${encodeURIComponent(userId)}&limit=500`);
            const list = Array.isArray(refreshed) ? refreshed : (refreshed?.conversations || []);
            const updated = list.find(c => c.id === savedConversation.id);
            if (updated && updated.processing === false) {
              setConversations(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
              return true;
            }
          } catch (e) { /* swallow — purely a polish refresh */ }
          return false;
        };
        setTimeout(async () => { if (!(await refreshAi())) setTimeout(refreshAi, 8000); }, 4000);
      }

      // Reset states
      setAudioBlob(null);
      setRecordingTime(0);
      setTranscribing(false);

      // Show success toast
      toast.success('Recording saved! AI summary is generating in the background…', { id: 'transcribe-toast' });
      
    } catch (err) {
      console.error('Error processing recording:', err);
      setTranscriptionError(err.message || 'Failed to process recording');
      setTranscribing(false);
      toast.error(err.message || 'Failed to save recording', { id: 'transcribe-toast' });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      setTranscriptionError('File size exceeds 100MB limit');
      return;
    }

    // Validate file format
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExt)) {
      setTranscriptionError(`Unsupported format. Please use: ${supportedFormats.join(', ')}`);
      return;
    }

    setSelectedFile(file);
    setTranscriptionError(null);
    setTranscription(null);
  };

  const handleTranscribe = async () => {
    if (!selectedFile) {
      setTranscriptionError('Please select an audio file');
      return;
    }

    setTranscribing(true);
    setTranscriptionError(null);

    try {
      // For now, just show success
      // In production, this would call the API
      alert('File uploaded! (In production, this would transcribe via API)');
      
      // Reload conversations
      await loadConversations();
      
      // Reset
      setSelectedFile(null);
      setTranscribing(false);
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionError(err.message || 'Failed to transcribe audio');
      setTranscribing(false);
    }
  };

  // Transcribe uploaded file from the transcription panel
  const transcribeUploadedFile = async () => {
    if (!uploadedFile) {
      setTranscriptionError('Please select an audio file');
      return;
    }

    setTranscribing(true);
    setTranscriptionError(null);

    try {
      // Step 1: Transcribe using Whisper with XMLHttpRequest
      console.log('🎤 Sending uploaded file for transcription...');
      toast.loading('Transcribing audio...', { id: 'transcribe-toast' });
      
      const transcribeResult = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/whisper/transcribe`, true);
        xhr.timeout = 120000; // 2 minute timeout
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error('Invalid transcription response'));
            }
          } else {
            reject(new Error(`Transcription failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during transcription'));
        xhr.ontimeout = () => reject(new Error('Transcription timed out'));
        
        const formData = new FormData();
        formData.append('audio', uploadedFile);
        xhr.send(formData);
      });
      
      console.log('✅ Transcription complete:', transcribeResult);
      
      toast.loading('Generating AI summary...', { id: 'transcribe-toast' });
      
      // Step 2: Convert audio file to base64 for storage
      const reader = new FileReader();
      const audioBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadedFile);
      });
      
      const fileName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      
      // Step 3: Save using QUICK SAVE — AI runs in background server-side.
      // Skip audio_data to keep the body small enough for production ingress.
      const savePayload = {
        transcript: transcribeResult.text || transcribeResult.transcript || '',
        duration: 0,
        language: transcribeResult.language || 'en',
        title: fileName,
        user_id: userId
      };

      console.log('💾 Saving conversation (quick save + background AI)...');

      // Use XMLHttpRequest for save
      const savedConversation = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/conversations-transcripts/?quick_save=true&user_id=${encodeURIComponent(userId)}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 60000;

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              resolve(result);
            } catch (e) {
              reject(new Error('Invalid save response'));
            }
          } else {
            reject(new Error(`Save failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during save'));
        xhr.ontimeout = () => reject(new Error('Save timed out'));

        xhr.send(JSON.stringify(savePayload));
      });

      console.log('✅ Conversation saved (AI processing in background):', savedConversation);

      // Add to state INSTANTLY with all required fields
      if (savedConversation && savedConversation.id) {
        const newConversation = {
          ...savedConversation,
          title: savedConversation.title || fileName,
          date: savedConversation.date || savedConversation.created_at || new Date().toISOString(),
          category: savedConversation.category || 'Meetings',
          duration: savedConversation.duration || '0:00'
        };
        setConversations(prev => [newConversation, ...prev.filter(c => c.id !== newConversation.id)]);
        console.log('✅ Added new conversation to list instantly');

        // Refetch shortly to pick up the background-generated AI title + summary
        const refreshAi = async () => {
          try {
            const refreshed = await apiCall(`/conversations-transcripts/?user_id=${encodeURIComponent(userId)}&limit=500`);
            const list = Array.isArray(refreshed) ? refreshed : (refreshed?.conversations || []);
            const updated = list.find(c => c.id === savedConversation.id);
            if (updated && updated.processing === false) {
              setConversations(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
              return true;
            }
          } catch (e) { /* polish refresh — non-fatal */ }
          return false;
        };
        setTimeout(async () => { if (!(await refreshAi())) setTimeout(refreshAi, 8000); }, 4000);
      }
      
      // Reset states
      setUploadedFile(null);
      setTranscribing(false);
      setShowUploadSection(false);
      
      toast.success(`Transcription saved with AI summary!`, { id: 'transcribe-toast' });
      
    } catch (err) {
      console.error('Error processing file:', err);
      setTranscriptionError(err.message || 'Failed to process file');
      setTranscribing(false);
      toast.error(err.message || 'Failed to transcribe file', { id: 'transcribe-toast' });
    }
  };

  const getMeetingStatus = (meeting) => {
    const meetingDateTime = new Date(`${meeting.date}T${meeting.time}`);
    const now = new Date();
    const meetingEnd = new Date(meetingDateTime.getTime() + (meeting.duration || 60) * 60000);

    if (now < meetingDateTime) {
      return 'upcoming';
    } else if (now >= meetingDateTime && now <= meetingEnd) {
      return 'in_progress';
    } else {
      return 'done';
    }
  };

  const parseTranscriptToMessages = (transcript) => {
    // Split by speaker pattern (e.g., "Darren:", "Person 2:")
    const lines = transcript.split('\n').filter(line => line.trim());
    const messages = [];
    
    lines.forEach(line => {
      const match = line.match(/^(.+?):\s*(.+)$/);
      if (match) {
        messages.push({
          speaker: match[1].trim(),
          text: match[2].trim()
        });
      } else if (messages.length > 0) {
        // Continue previous message
        messages[messages.length - 1].text += ' ' + line.trim();
      }
    });
    
    return messages;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tempId = `temp-${Date.now()}`;
    try {
      let meetingId;
      if (isEditing && editingMeetingId) {
        await apiCall(`/meetings/${editingMeetingId}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        meetingId = editingMeetingId;
        // Update local state for edited project
        setMeetings(prev => prev.map(m => m.id === meetingId ? { ...m, ...formData, organization: formData.title } : m));
        const stagesUpdate = { ...meetingStatuses, [meetingId]: formData.pipeline_stage };
        setMeetingStatuses(stagesUpdate);
        localStorage.setItem('meeting_statuses', JSON.stringify(stagesUpdate));
      } else {
        // ── Optimistic update: show card immediately in the correct column ──
        const optimisticProject = {
          id: tempId,
          title: formData.title,
          organization: formData.title,
          date: formData.date || '',
          time: formData.time || '',
          duration: formData.duration,
          participants: formData.participants || [],
          documents: formDocuments || [],
          status: 'scheduled',
          category: formData.category,
          location: formData.location || '',
          notes: formData.notes || '',
          agenda: formData.agenda || '',
          pipeline_stage: formData.pipeline_stage,
          priority: formData.priority,
          type: formData.type,
          created_at: new Date().toISOString()
        };
        setMeetings(prev => [...prev, optimisticProject]);
        const stagesUpdate = { ...meetingStatuses, [tempId]: formData.pipeline_stage };
        setMeetingStatuses(stagesUpdate);
        localStorage.setItem('meeting_statuses', JSON.stringify(stagesUpdate));

        // Save to backend
        const response = await apiCall(`/meetings/?user_id=${encodeURIComponent(userId)}`, {
          method: 'POST',
          body: JSON.stringify({ ...formData, organization: formData.title, documents: formDocuments })
        });
        meetingId = response.id;

        // Replace temp ID with real ID
        setMeetings(prev => prev.map(m => m.id === tempId ? { ...m, id: meetingId } : m));
        const finalStages = { ...meetingStatuses };
        delete finalStages[tempId];
        finalStages[meetingId] = formData.pipeline_stage;
        setMeetingStatuses(finalStages);
        localStorage.setItem('meeting_statuses', JSON.stringify(finalStages));

        // Link to transcription if applicable
        if (formData.transcription_id) {
          try {
            await apiCall(`/conversations-transcripts/${formData.transcription_id}`, {
              method: 'PATCH',
              body: JSON.stringify({ meeting_id: meetingId })
            });
          } catch (linkError) {
            console.error('Failed to link project to conversation:', linkError);
          }
        }
      }
      resetForm();
    } catch (error) {
      // Roll back optimistic update on failure
      setMeetings(prev => prev.filter(m => m.id !== tempId));
      setMeetingStatuses(prev => { const s = { ...prev }; delete s[tempId]; return s; });
      console.error('Error saving project:', error);
      alert('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    // Calendar-sourced meetings are read-only; tell the user where to delete them.
    if (typeof id === 'string' && id.startsWith('cal:')) {
      toast.info('This is a synced Calendar event. Delete it from your Calendar app — it will disappear here on the next sync.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    
    try {
      await apiCall(`/meetings/${id}`, { method: 'DELETE' });
      // Remove from local state immediately
      setMeetings(prev => prev.filter(m => m.id !== id));
      setPersonalMeetings(prev => prev.filter(m => m.id !== id));
      toast.success('Meeting deleted successfully');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Failed to delete meeting');
    }
  };

  // Handle file upload for new project/meeting form
  const handleFormDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingFormDoc(true);
    const uploadedDocs = [];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const uploadResponse = await apiUpload('/uploads/documents', formDataUpload);
        
        if (uploadResponse && uploadResponse.url) {
          uploadedDocs.push({
            id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            filename: uploadResponse.filename || file.name,
            original_filename: file.name,
            url: uploadResponse.url,
            content_type: file.type,
            size: `${(file.size / 1024).toFixed(1)} KB`,
            uploaded_at: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadedDocs.length > 0) {
      setFormDocuments(prev => [...prev, ...uploadedDocs]);
      toast.success(`Uploaded ${uploadedDocs.length} file(s)`);
    }
    setUploadingFormDoc(false);
    e.target.value = ''; // Reset file input
  };

  const removeFormDocument = (docId) => {
    setFormDocuments(prev => prev.filter(d => d.id !== docId));
  };

  // Handle delete for both Excel and regular meetings
  const handleDeleteMeeting = async (meeting) => {
    // Calendar-sourced meetings are read-only
    if (meeting && (meeting.source === 'calendar' || (typeof meeting.id === 'string' && meeting.id.startsWith('cal:')))) {
      toast.info('This is a synced Calendar event. Delete it from your Calendar app — it will disappear here on the next sync.');
      return;
    }
    try {
      // Check if it's an Excel meeting
      if (meeting.source === 'desktop_excel' || meeting.excel_meeting_id) {
        // Delete from Excel backend
        const response = await apiCall(`/excel/delete-meeting/${meeting.id}`, { method: 'DELETE' });
        if (response) {
          // Remove from local state
          setMeetings(prev => prev.filter(m => m.id !== meeting.id));
          setExcelMeetings(prev => prev.filter(m => m.id !== meeting.id));
          console.log(`✅ Deleted Excel meeting: ${meeting.title || meeting.organization}`);
          toast.success('Meeting deleted successfully');
        }
      } else {
        // Delete from regular meetings API (Personal meetings)
        await apiCall(`/meetings/${meeting.id}`, { method: 'DELETE' });
        // Remove from local state immediately
        setMeetings(prev => prev.filter(m => m.id !== meeting.id));
        setPersonalMeetings(prev => prev.filter(m => m.id !== meeting.id));
        console.log(`✅ Deleted personal meeting: ${meeting.title || meeting.organization}`);
        toast.success('Meeting deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Failed to delete meeting. Please try again.');
    }
  };

  const handleEdit = (meeting) => {
    // Normalize category: if stored category doesn't match any available tab,
    // default to first valid category so the select always shows a real value.
    const validCategoryNames = customMeetingCategories
      .filter(c => c.id !== 'all')
      .map(c => c.name);
    const storedCategory = meeting.category || '';
    const isValidCategory = validCategoryNames.some(
      name => name.trim().toLowerCase() === storedCategory.trim().toLowerCase()
    );
    const normalizedCategory = isValidCategory
      ? storedCategory
      : (validCategoryNames[0] || 'Real Estate');

    setFormData({
      title: meeting.title,
      date: meeting.date,
      time: meeting.time,
      duration: meeting.duration || 60,
      meeting_link: meeting.meeting_link || '',
      location: meeting.location || '',
      meeting_type: meeting.meeting_type || (meeting.meeting_link ? 'virtual' : 'in-person'),
      category: normalizedCategory,
      notes: meeting.notes || '',
      agenda: meeting.agenda || meeting.notes || '',  // fall back to notes for legacy data
      transcript: meeting.transcript || '',
      transcription_id: meeting.transcription_id || '',
      participants: meeting.participants || [],
      pipeline_stage: meeting.pipeline_stage || 'tbd',
      priority: meeting.priority || 'medium',
      type: meeting.type || 'project'
    });
    setEditingMeetingId(meeting.id);
    setIsEditing(true);
  };

  const resetForm = () => {
    const defaultCategory = customMeetingCategories.find(c => c.id !== 'all')?.name || 'Real Estate';
    setFormData({
      title: '',
      date: '',
      time: '',
      duration: 60,
      meeting_link: '',
      location: '',
      meeting_type: 'virtual',
      category: defaultCategory,
      notes: '',
      agenda: '',
      transcript: '',
      transcription_id: '',
      participants: [],
      documents: [],
      pipeline_stage: 'tbd',
      priority: 'medium',
      type: mainTab === 'meetings' ? 'meeting' : 'project'
    });
    setFormDocuments([]);
    setNewParticipant({
      name: '',
      email: '',
      company: '',
      role: '',
      phone: '',
      image_url: '',
      image_file: null
    });
    setIsCreating(false);
    setIsEditing(false);
    setEditingMeetingId(null);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const data = await apiUpload('/uploads/thumbnail', formData);
      console.log('✅ Image uploaded successfully:', data);
      return data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Document upload handler for meetings
  const handleDocumentUpload = async (file, meetingId) => {
    if (!file || !meetingId) return null;
    
    // Validate file format
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedDocFormats.includes(fileExt)) {
      alert(`Unsupported file format. Supported: ${supportedDocFormats.join(', ')}`);
      return null;
    }
    
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB');
      return null;
    }
    
    setUploadingDocument(true);
    setDocumentUploadMeetingId(meetingId);
    
    try {
      // Step 1: Upload file to storage
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await apiUpload('/uploads/documents', formData);
      console.log('✅ Document uploaded to storage:', uploadResponse);
      
      // Step 2: Add document reference to meeting
      const docData = {
        filename: uploadResponse.filename,
        original_filename: uploadResponse.original_filename,
        url: uploadResponse.url,
        content_type: uploadResponse.content_type,
        size: uploadResponse.size
      };
      
      const response = await apiCall(`/meetings/${meetingId}/documents`, {
        method: 'POST',
        body: JSON.stringify(docData)
      });
      
      console.log('✅ Document linked to meeting:', response);
      
      // Update local state
      if (selectedMeeting && selectedMeeting.id === meetingId) {
        const updatedDocs = [...(selectedMeeting.documents || []), response.document];
        setSelectedMeeting({ ...selectedMeeting, documents: updatedDocs });
      }
      
      // Update meetings list
      setMeetings(prevMeetings => prevMeetings.map(m => {
        if (m.id === meetingId) {
          return { ...m, documents: [...(m.documents || []), response.document] };
        }
        return m;
      }));
      
      return response.document;
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + error.message);
      return null;
    } finally {
      setUploadingDocument(false);
      setDocumentUploadMeetingId(null);
    }
  };

  // Remove document from meeting
  const handleRemoveDocument = async (meetingId, documentId) => {
    if (!window.confirm('Are you sure you want to remove this document?')) return;
    
    try {
      await apiCall(`/meetings/${meetingId}/documents/${documentId}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Document removed from meeting');
      
      // Update local state
      if (selectedMeeting && selectedMeeting.id === meetingId) {
        const updatedDocs = (selectedMeeting.documents || []).filter(d => d.id !== documentId);
        setSelectedMeeting({ ...selectedMeeting, documents: updatedDocs });
      }
      
      // Update meetings list
      setMeetings(prevMeetings => prevMeetings.map(m => {
        if (m.id === meetingId) {
          return { ...m, documents: (m.documents || []).filter(d => d.id !== documentId) };
        }
        return m;
      }));
    } catch (error) {
      console.error('Error removing document:', error);
      alert('Failed to remove document: ' + error.message);
    }
  };

  // Get file icon based on content type
  const getDocumentIcon = (contentType, filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (contentType?.includes('pdf') || ext === 'pdf') return '📄';
    if (contentType?.includes('word') || ['doc', 'docx'].includes(ext)) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) return '📊';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation') || ['ppt', 'pptx'].includes(ext)) return '📽️';
    if (contentType?.includes('image') || ['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return '🖼️';
    if (contentType?.includes('text') || ext === 'txt') return '📃';
    return '📎';
  };

  const addParticipant = async () => {
    if (newParticipant.name.trim()) {
      let imageUrl = newParticipant.image_url;
      
      // If a file was selected, upload it first
      if (newParticipant.image_file) {
        imageUrl = await handleImageUpload(newParticipant.image_file);
      }
      
      setFormData({
        ...formData,
        participants: [...formData.participants, { 
          name: newParticipant.name,
          email: newParticipant.email,
          company: newParticipant.company,
          role: newParticipant.role,
          phone: newParticipant.phone,
          image_url: imageUrl || ''
        }]
      });
      setNewParticipant({
        name: '',
        email: '',
        company: '',
        role: '',
        phone: '',
        image_url: '',
        image_file: null
      });
    }
  };

  const removeParticipant = (index) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter((_, i) => i !== index)
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      const date = parseLocalDate(dateString);
      if (!date) return 'TBD';
      const formatted = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        weekday: 'short'
      });
      return formatted === 'Invalid Date' ? 'TBD' : formatted;
    } catch {
      return 'TBD';
    }
  };

  const formatTimeCST = (timeString) => {
    if (!timeString) return '';
    // If it's a full datetime string
    if (timeString.includes('T') || timeString.includes('-')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    // If it's just a time string like "14:30"
    const [hours, minutes] = timeString.split(':');
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${ampm}`;
  };

  const getMeetingLinkType = (link) => {
    if (!link) return null;
    if (link.includes('zoom.us')) return 'Zoom';
    if (link.includes('teams.microsoft.com') || link.includes('teams.live.com')) return 'Teams';
    return 'Meeting';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesStatus = filterStatus === 'all' || getMeetingDisplayStatus(meeting) === filterStatus;
    const matchesCategory = selectedCategory === 'all' || (meeting.category || 'Real Estate').trim().toLowerCase() === selectedCategory.trim().toLowerCase();
    
    // Search filter - check title, organization, location, notes, agenda, participants
    const searchLower = searchQuery.toLowerCase().trim();
    const matchesSearch = !searchLower || 
      (meeting.title || '').toLowerCase().includes(searchLower) ||
      (meeting.organization || '').toLowerCase().includes(searchLower) ||
      (meeting.location || '').toLowerCase().includes(searchLower) ||
      (meeting.notes || '').toLowerCase().includes(searchLower) ||
      (meeting.agenda || '').toLowerCase().includes(searchLower) ||
      (meeting.participants || []).some(p => (p.name || '').toLowerCase().includes(searchLower));
    
    // Filter by main tab (all, projects, meetings)
    // If no type field, fall back to checking category name for "meeting" keyword
    const rawType = meeting.type;
    const itemType = rawType || (
      ['Meeting', 'Meetings', 'meeting', 'meetings'].includes(meeting.category)
        ? 'meeting'
        : 'project'
    );
    const matchesMainTab = mainTab === 'all' || 
      (mainTab === 'projects' && (itemType === 'project' || itemType === 'personal')) ||
      (mainTab === 'meetings' && itemType === 'meeting');
    
    return matchesStatus && matchesCategory && matchesMainTab && matchesSearch;
  }).sort((a, b) => {
    let comparison = 0;
    
    if (sortField === 'date') {
      // Check if dates are TBD or invalid
      const isTBD_A = !a.date || a.date === 'TBD' || a.date === '-' || a.date === '';
      const isTBD_B = !b.date || b.date === 'TBD' || b.date === '-' || b.date === '';
      
      // TBD meetings always go to the bottom
      if (isTBD_A && !isTBD_B) return 1;  // A is TBD, goes after B
      if (!isTBD_A && isTBD_B) return -1; // B is TBD, goes after A
      if (isTBD_A && isTBD_B) return 0;   // Both TBD, keep original order
      
      // Parse Excel decimal time format
      const parseTime = (timeVal) => {
        if (!timeVal || timeVal === 'TBD' || timeVal === '-') return '00:00';
        if (typeof timeVal === 'number' || !isNaN(parseFloat(timeVal))) {
          const decimal = parseFloat(timeVal);
          const hours = Math.floor(decimal * 24);
          const minutes = Math.round((decimal * 24 - hours) * 60);
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        return timeVal;
      };
      
      const datePart_A = String(a.date).split('T')[0].split(' ')[0];
      const datePart_B = String(b.date).split('T')[0].split(' ')[0];
      const timeStrA = parseTime(a.time);
      const timeStrB = parseTime(b.time);
      
      const dateA = new Date(`${datePart_A}T${timeStrA}`);
      const dateB = new Date(`${datePart_B}T${timeStrB}`);
      
      // Handle invalid dates
      const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      
      comparison = timeA - timeB;
    } else if (sortField === 'name' || sortField === 'title') {
      // A-Z sorting by title/organization
      const nameA = (a.organization || a.title || '').toLowerCase();
      const nameB = (b.organization || b.title || '').toLowerCase();
      comparison = nameA.localeCompare(nameB);
    } else if (sortField === 'created') {
      // Sort by created_at (date added)
      const createdA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const createdB = b.created_at ? new Date(b.created_at).getTime() : 0;
      comparison = createdA - createdB;
    } else if (sortField === 'status') {
      // Sort by status
      const statusOrder = { 'done': 0, 'in_progress': 1, 'upcoming': 2, 'tbd': 3 };
      const statusA = statusOrder[getMeetingDisplayStatus(a)] ?? 4;
      const statusB = statusOrder[getMeetingDisplayStatus(b)] ?? 4;
      comparison = statusA - statusB;
    }
    
    // desc = newest first (larger dates first), asc = oldest first
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white">Loading meetings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 md:p-6">
      <div className="max-w-[98%] mx-auto px-2 md:px-4">
        {/* Header - Outside two-column layout */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <Calendar className="text-white" size={24} />
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">
                {mainTab === 'all' ? 'All Projects & Meetings' : mainTab === 'projects' ? 'Projects & Meetings' : 'Meetings'}
              </h1>
              {lastSync && (
                <p className="text-xs md:text-sm text-gray-400 mt-1">
                  Last synced: {lastSync.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Main Tabs - All / Projects / Meetings - Outside two-column layout */}
        {/* Hide Projects tab for specific users */}
        {(() => {
          const allTabs = [
            { id: 'all', label: 'All', icon: '📋' },
            { id: 'projects', label: 'Projects', icon: '📁' },
            { id: 'meetings', label: 'Meetings', icon: '📅' }
          ];
          // Users who should not see the Projects tab
          const hideProjectsTabForUsers = ['dwoodson@cresa.com', 'darren@cresa.com', 'darren@operatr.com', 'counterfind@operatr.com'];
          const visibleTabs = hideProjectsTabForUsers.includes(user?.email?.toLowerCase())
            ? allTabs.filter(tab => tab.id !== 'projects')
            : allTabs;
          
          return (
            <div className="flex items-center gap-1 mb-4 bg-black border border-white/20 rounded-xl p-1 w-fit">
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setMainTab(tab.id)}
                  data-testid={`main-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    mainTab === tab.id
                      ? 'bg-white text-black'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Main Layout - Full Width */}
        <div className="w-full">
          {/* Projects Column */}
          <div className="w-full">

        {/* Filter Buttons */}
        <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 mb-4 md:mb-6 space-y-3 md:space-y-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          {/* Category Row - Category filters on left, Action buttons on right */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Category Filters */}
            <div className="flex-1">
              <label className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">Category</label>
              <div className="flex gap-1.5 md:gap-2 flex-wrap items-center">
                {customMeetingCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id === 'all' ? 'all' : cat.name)}
                    data-testid={`meeting-category-${cat.id}`}
                    className={`px-2 md:px-4 py-1 md:py-2 rounded-lg text-xs md:text-sm transition-colors flex items-center gap-1.5 ${
                      selectedCategory === (cat.id === 'all' ? 'all' : cat.name)
                        ? 'bg-white text-black'
                        : 'bg-white/5 text-white hover:bg-white/10'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    {cat.name}
                  </button>
                ))}
                
                {/* Edit Categories Button */}
                <button
                  onClick={() => setShowMeetingCategoryModal(true)}
                  data-testid="edit-meeting-categories-btn"
                  className="px-2 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
                  title="Edit Categories"
                >
                  <Pencil size={12} />
                  <span className="hidden md:inline">Edit</span>
                </button>
              </div>
            </div>
            
            {/* Action Buttons - Right side */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Document Upload Button */}
              <button
                onClick={() => setShowDocumentUploadModal(true)}
                data-testid="upload-meetings-btn"
                className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium bg-gray-800 text-white border border-gray-600 hover:bg-gray-700"
              >
                <Upload size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">Upload</span>
              </button>
              
              {/* Google Sheets Integration Button */}
              <button
                onClick={() => setShowSheetsModal(true)}
                data-testid="google-sheets-btn"
                className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium ${
                  sheetsConnected
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30'
                    : 'bg-gray-800 text-white border border-gray-600 hover:bg-gray-700'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
                  <path d="M19 3H5C3.89 3 3 3.89 3 5V19C3 20.11 3.89 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.89 20.11 3 19 3M19 19H5V5H19V19M7 7H17V9H7V7M7 11H17V13H7V11M7 15H14V17H7V15" />
                </svg>
                <span className="hidden sm:inline">{sheetsConnected ? 'Sheets Connected' : 'Google Sheets'}</span>
                <span className="sm:hidden">Sheets</span>
              </button>
              
              {/* View Toggle */}
              <div className="flex items-center gap-1 md:gap-2 bg-black border border-gray-600 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-sm md:text-base ${
                    viewMode === 'grid' || viewMode === 'kanban'
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-sm md:text-base ${
                    viewMode === 'list'
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  List
                </button>
              </div>
              
              {/* New Project/Meeting Button */}
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, type: mainTab === 'meetings' ? 'meeting' : 'project' }));
                  setIsCreating(true);
                }}
                data-testid="new-project-btn"
                className="flex items-center gap-1.5 md:gap-2 bg-white hover:bg-gray-100 text-black px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">{mainTab === 'meetings' ? 'New Meeting' : 'New Project'}</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search meetings, participants, locations..."
              className="w-full pl-10 pr-4 py-2.5 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-gray-400 transition-colors"
              data-testid="search-meetings-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs hidden sm:inline">Sort:</span>
            <select
              value={`${sortField}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortField(field);
                setSortOrder(order);
              }}
              className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gray-400"
              data-testid="sort-meetings-select"
            >
              <option value="date-desc">Date (Newest First)</option>
              <option value="date-asc">Date (Oldest First)</option>
              <option value="title-asc">A → Z</option>
              <option value="title-desc">Z → A</option>
              <option value="created-desc">Recently Added</option>
              <option value="created-asc">Oldest Added</option>
              <option value="status-asc">Status</option>
            </select>
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs hidden sm:inline">Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gray-400"
              data-testid="status-filter-select"
            >
              <option value="all">All Status</option>
              <option value="tbd">📋 TBD</option>
              <option value="upcoming">📅 Upcoming</option>
              <option value="in_progress">🔄 In Progress</option>
              <option value="done">✅ Done</option>
            </select>
          </div>
          
          {/* Results Count */}
          <div className="text-gray-500 text-xs sm:ml-auto">
            {filteredMeetings.length} {filteredMeetings.length === 1 ? 'result' : 'results'}
            {searchQuery && ` for "${searchQuery}"`}
          </div>
        </div>

        {/* Card Grid View (Simple grid like Contacts page) */}
        {(viewMode === 'kanban' || viewMode === 'grid') && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredMeetings.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Calendar className="mx-auto text-gray-600 mb-3" size={48} />
                <p className="text-gray-400 text-lg">No {mainTab === 'meetings' ? 'meetings' : mainTab === 'projects' ? 'projects' : 'items'} found</p>
                <p className="text-gray-500 text-sm mt-1">Click &quot;New {mainTab === 'meetings' ? 'Meeting' : 'Project'}&quot; to create one</p>
              </div>
            ) : (
              filteredMeetings.map((meeting) => {
                const status = getMeetingDisplayStatus(meeting);
                const primaryParticipant = meeting.participants && meeting.participants.length > 0 
                  ? meeting.participants[0] 
                  : null;
                
                // Get linked transcription
                const linkedTranscriptionId = meeting.transcription_id || meetingTranscriptions[meeting.id];
                const linkedTranscription = linkedTranscriptionId 
                  ? conversations.find(c => c.id === linkedTranscriptionId)
                  : null;
                
                // Get folder transcriptions count
                const folderCount = meeting.transcription_folder?.transcription_ids?.length || 0;
                
                // Get status color
                const statusColors = {
                  done: 'bg-green-600/30 text-green-300',
                  in_progress: 'bg-orange-600/30 text-orange-300',
                  upcoming: 'bg-yellow-600/30 text-yellow-300',
                  tbd: 'bg-blue-600/30 text-blue-300'
                };
                
                return (
                  <div
                    key={meeting.id}
                    className="bg-black border border-white/20 rounded-xl overflow-hidden hover:border-white/40 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-200"
                    data-testid={`meeting-card-${meeting.id}`}
                  >
                    {/* Card Header - Clickable to expand */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => {
                        const isExpanding = !expandedMeetings[meeting.id];
                        setExpandedMeetings(prev => ({ ...prev, [meeting.id]: isExpanding }));
                        // If expanding and there's a linked transcription, load its breakdown
                        if (isExpanding && linkedTranscription) {
                          loadBreakdownForTranscript(linkedTranscription);
                        }
                      }}
                    >
                      {/* Header with avatar and title */}
                      <div className="flex items-start gap-3 mb-3">
                        {primaryParticipant?.image_url ? (
                          <img 
                            src={primaryParticipant.image_url} 
                            alt={primaryParticipant.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {(meeting.organization || meeting.title || 'M').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-sm truncate">
                            {meeting.organization || meeting.title || 'Untitled'}
                          </h3>
                          {primaryParticipant && (
                            <p className="text-gray-400 text-xs truncate">{primaryParticipant.name}</p>
                          )}
                        </div>
                        {expandedMeetings[meeting.id] ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                      
                      {/* Date and Time */}
                      <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                        <Calendar size={12} />
                        <span>{formatDate(meeting.date)}</span>
                        {meeting.time && (
                          <>
                            <Clock size={12} />
                            <span>{formatTimeTo12Hour(meeting.time)}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Location */}
                      {meeting.location && (
                        <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                          <MapPin size={12} />
                          <span className="truncate">{meeting.location}</span>
                        </div>
                      )}
                      
                      {/* Linked Transcription */}
                      {(linkedTranscription || folderCount > 0) && (
                        <div className="flex items-center gap-2 text-purple-400 text-xs mb-2 bg-purple-500/10 border border-purple-500/20 rounded-lg px-2 py-1.5">
                          <Mic size={12} />
                          <span className="truncate flex-1">
                            {linkedTranscription?.title || `${folderCount} transcription${folderCount !== 1 ? 's' : ''}`}
                          </span>
                          {linkedTranscription?.ai_breakdown && (
                            <Sparkles size={10} className="text-purple-300" title="Has AI Breakdown" />
                          )}
                        </div>
                      )}
                      
                      {/* Agenda/Notes preview */}
                      {(meeting.agenda || meeting.notes) && !linkedTranscription && !expandedMeetings[meeting.id] && (
                        <p className="text-gray-500 text-xs line-clamp-2 mb-3">
                          {meeting.agenda || meeting.notes}
                        </p>
                      )}
                      
                      {/* Status Badge */}
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-semibold px-2 py-1 rounded ${statusColors[status] || statusColors.tbd}`}>
                          {status === 'done' ? '✅ Done' : 
                           status === 'in_progress' ? '🔄 In Progress' : 
                           status === 'upcoming' ? '📅 Upcoming' : '📋 TBD'}
                        </span>
                        
                        {/* Meeting type indicator */}
                        <span className="text-gray-500 text-[10px]">
                          {meeting.meeting_type === 'virtual' ? '💻 Virtual' : '🏢 In-Person'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Expanded Content - Shows instantly when clicked */}
                    {expandedMeetings[meeting.id] && (
                      <div className="border-t border-white/10">
                        {/* Participants Section */}
                        {meeting.participants?.length > 0 && (
                          <div className="p-4 border-b border-white/10 bg-white/5">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                              <Users size={10} /> Participants ({meeting.participants.length})
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {meeting.participants.map((participant, i) => (
                                <div key={i} className="flex items-center gap-2 bg-black border border-white/10 rounded-lg px-2 py-1">
                                  {participant.image_url ? (
                                    <img src={participant.image_url} alt={participant.name} className="w-6 h-6 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-purple-600/30 flex items-center justify-center text-[10px] text-purple-300 font-bold">
                                      {(participant.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <span className="text-xs text-gray-300">{participant.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Agenda/Notes Full */}
                        {(meeting.agenda || meeting.notes) && (
                          <div className="p-4 border-b border-white/10">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
                              {meeting.agenda ? 'Agenda' : 'Notes'}
                            </div>
                            <p className="text-sm text-gray-300 whitespace-pre-wrap">{meeting.agenda || meeting.notes}</p>
                          </div>
                        )}
                        
                        {/* Linked Transcription AI Breakdown */}
                        {linkedTranscription && (
                          <div className="p-4 bg-purple-500/5">
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-1">
                                <Mic size={10} /> Linked Transcription
                              </div>
                              <span className="text-xs text-gray-400">{linkedTranscription.title}</span>
                            </div>
                            
                            {/* Audio if available */}
                            {linkedTranscription.audio_url && (
                              <div className="mb-4">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                                  <FileAudio size={10} /> Audio
                                </div>
                                <audio 
                                  src={linkedTranscription.audio_url} 
                                  controls 
                                  className="w-full h-10"
                                  style={{ filter: 'invert(1)' }}
                                />
                              </div>
                            )}
                            
                            {/* Attendees from transcription */}
                            {linkedTranscription.attendees?.length > 0 && (
                              <div className="mb-4">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                                  <Users size={10} /> In Transcription ({linkedTranscription.attendees.length})
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {linkedTranscription.attendees.map((att, i) => (
                                    <span key={i} className="text-xs px-2 py-0.5 bg-black border border-white/10 rounded text-gray-300">
                                      {att.name || att}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* AI Breakdown */}
                            {(() => {
                              const breakdown = loadedBreakdowns[linkedTranscription.id] || linkedTranscription.ai_breakdown;
                              const isLoading = loadingBreakdowns[linkedTranscription.id];
                              
                              if (isLoading) {
                                return (
                                  <div className="flex items-center gap-2 text-purple-300 text-sm py-4">
                                    <Loader2 size={16} className="animate-spin" />
                                    Loading AI breakdown...
                                  </div>
                                );
                              }
                              
                              if (breakdown) {
                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Sparkles size={12} className="text-purple-400" />
                                      <span className="text-[10px] uppercase tracking-wider text-purple-300">AI Breakdown</span>
                                      {breakdown.sentiment && (
                                        <span className="text-[10px] bg-purple-500/20 text-purple-200 px-2 py-0.5 rounded-full ml-auto">
                                          {breakdown.sentiment}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {breakdown.overview && (
                                      <div>
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Overview</div>
                                        <p className="text-sm text-gray-200">{breakdown.overview}</p>
                                      </div>
                                    )}
                                    
                                    {breakdown.key_topics?.length > 0 && (
                                      <div>
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Topics</div>
                                        <div className="flex flex-wrap gap-1">
                                          {breakdown.key_topics.map((t, i) => (
                                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-300">{t}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {breakdown.action_items?.length > 0 && (
                                      <div>
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Action Items</div>
                                        <ul className="space-y-1 text-sm">
                                          {breakdown.action_items.map((a, i) => {
                                            const text = typeof a === 'string' ? a : (a?.text || JSON.stringify(a));
                                            return (
                                              <li key={i} className="flex items-start gap-2 text-gray-200">
                                                <CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" />
                                                <span>{text}</span>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {breakdown.key_decisions?.length > 0 && (
                                      <div>
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Key Decisions</div>
                                        <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                                          {breakdown.key_decisions.map((d, i) => <li key={i}>{d}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              
                              return (
                                <div className="text-center text-gray-500 text-sm py-4">
                                  <Sparkles size={16} className="mx-auto mb-2 opacity-50" />
                                  No AI breakdown available
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* Edit Button */}
                        <div className="p-3 border-t border-white/10 flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedMeeting(meeting); }}
                            className="flex-1 py-2 text-xs font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            View Details
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(meeting); }}
                            className="px-4 py-2 text-xs font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <Edit2 size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Transcription Cards Section - Shows all transcriptions with AI Breakdowns */}
        {(viewMode === 'kanban' || viewMode === 'grid') && conversations.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Mic size={18} className="text-purple-400" />
                Transcriptions
                <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full ml-2">{conversations.length}</span>
              </h3>
              
              {/* Pagination Info */}
              {conversations.length > TRANSCRIPTIONS_PER_PAGE && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>Page {transcriptionPage} of {Math.ceil(conversations.length / TRANSCRIPTIONS_PER_PAGE)}</span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {conversations
                .slice((transcriptionPage - 1) * TRANSCRIPTIONS_PER_PAGE, transcriptionPage * TRANSCRIPTIONS_PER_PAGE)
                .map((transcript) => {
                // Find linked meeting
                const linkedMeeting = meetings.find(m => 
                  m.transcription_id === transcript.id || 
                  meetingTranscriptions[m.id] === transcript.id ||
                  m.transcription_folder?.transcription_ids?.includes(transcript.id)
                );
                const breakdown = transcript.ai_breakdown;
                const isExpanded = expandedTranscripts[transcript.id];
                
                return (
                  <div
                    key={transcript.id}
                    className="bg-black border border-white/20 rounded-xl overflow-hidden hover:border-purple-500/50 hover:shadow-[0_0_20px_rgba(168,85,247,0.15)] transition-all duration-200"
                    data-testid={`transcript-card-${transcript.id}`}
                  >
                    {/* Card Header - Clickable to expand */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => setExpandedTranscripts(prev => ({ ...prev, [transcript.id]: !prev[transcript.id] }))}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-600/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
                          <Mic size={16} className="text-purple-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-semibold text-sm truncate">
                            {transcript.title || 'Untitled Transcription'}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-gray-500 text-xs">
                              {transcript.date ? new Date(transcript.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                            </span>
                            {transcript.source === 'fireflies' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-300 rounded">Fireflies</span>
                            )}
                            {transcript.source === 'manual' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">Manual</span>
                            )}
                            {linkedMeeting && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded flex items-center gap-1">
                                <Calendar size={8} /> {linkedMeeting.organization || linkedMeeting.title || 'Meeting'}
                              </span>
                            )}
                          </div>
                          
                          {/* Participants in meeting/transcription */}
                          {transcript.attendees?.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              <Users size={10} className="text-gray-500" />
                              {transcript.attendees.slice(0, 3).map((att, i) => (
                                <span key={i} className="text-[10px] text-gray-400">{att.name || att}{i < Math.min(transcript.attendees.length, 3) - 1 ? ',' : ''}</span>
                              ))}
                              {transcript.attendees.length > 3 && (
                                <span className="text-[10px] text-gray-500">+{transcript.attendees.length - 3} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {breakdown && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded text-purple-300 text-[10px]">
                              <Sparkles size={10} /> AI
                            </div>
                          )}
                          {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Content - AI Breakdown, Audio, Participants */}
                    {isExpanded && (
                      <div className="border-t border-white/10">
                        {/* Participants Section */}
                        {transcript.attendees?.length > 0 && (
                          <div className="p-4 border-b border-white/10 bg-white/5">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                              <Users size={10} /> Participants ({transcript.attendees.length})
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {transcript.attendees.map((att, i) => (
                                <div key={i} className="flex items-center gap-2 bg-black border border-white/10 rounded-lg px-2 py-1">
                                  <div className="w-6 h-6 rounded-full bg-purple-600/30 flex items-center justify-center text-[10px] text-purple-300 font-bold">
                                    {(att.name || att || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-xs text-gray-300">{att.name || att}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Audio Player if available */}
                        {transcript.audio_url && (
                          <div className="p-4 border-b border-white/10 bg-white/5">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                              <FileAudio size={10} /> Audio Recording
                            </div>
                            <audio 
                              src={transcript.audio_url} 
                              controls 
                              className="w-full h-10"
                              style={{ filter: 'invert(1)' }}
                            />
                          </div>
                        )}
                        
                        {/* AI Breakdown Section */}
                        {breakdown && (
                          <div className="p-4 bg-purple-500/5 space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-1">
                                <Sparkles size={10} /> AI Breakdown
                              </div>
                              {breakdown.sentiment && (
                                <span className="text-[10px] uppercase tracking-wider text-purple-200 bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 rounded-full">
                                  {breakdown.sentiment}
                                </span>
                              )}
                            </div>
                            
                            {breakdown.overview && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Overview</div>
                                <p className="text-sm text-gray-200 leading-relaxed">{breakdown.overview}</p>
                              </div>
                            )}
                            
                            {breakdown.key_topics?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Topics</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {breakdown.key_topics.map((t, i) => (
                                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-300">{t}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {breakdown.key_decisions?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Key Decisions</div>
                                <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                                  {breakdown.key_decisions.map((d, i) => <li key={i}>{d}</li>)}
                                </ul>
                              </div>
                            )}
                            
                            {breakdown.action_items?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Action Items</div>
                                <ul className="space-y-1.5 text-sm">
                                  {breakdown.action_items.map((a, i) => {
                                    const text = typeof a === 'string' ? a : (a?.text || JSON.stringify(a));
                                    const owner = (typeof a === 'object' && a) ? a.owner : null;
                                    const due = (typeof a === 'object' && a) ? a.due : null;
                                    return (
                                      <li key={i} className="flex flex-col text-gray-200">
                                        <span className="flex items-center gap-2">
                                          <CheckCircle size={12} className="text-green-400 flex-shrink-0" />
                                          {text}
                                        </span>
                                        {owner && owner !== 'Unassigned' && (
                                          <span className="text-[11px] text-gray-500 ml-5">→ {owner}{due ? ` · ${due}` : ''}</span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                            
                            {breakdown.open_questions?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Open Questions</div>
                                <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                                  {breakdown.open_questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ul>
                              </div>
                            )}
                            
                            {breakdown.speaker_breakdown?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Speaker Contributions</div>
                                <div className="space-y-2">
                                  {breakdown.speaker_breakdown.map((sp, i) => (
                                    <div key={i} className="text-sm bg-black/30 rounded-lg p-2">
                                      <span className="text-purple-300 font-medium">{sp.name}: </span>
                                      <span className="text-gray-300">{sp.summary}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {breakdown.next_steps?.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Next Steps</div>
                                <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                                  {breakdown.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* No AI Breakdown placeholder */}
                        {!breakdown && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            <Sparkles size={16} className="mx-auto mb-2 opacity-50" />
                            No AI breakdown available for this transcription
                          </div>
                        )}
                        
                        {/* View Full Transcript Button */}
                        <div className="p-3 border-t border-white/10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingTranscription(transcript);
                              setShowTranscriptionPopup(true);
                            }}
                            className="w-full py-2 text-xs font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            View Full Transcript Text
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Pagination Controls */}
            {conversations.length > TRANSCRIPTIONS_PER_PAGE && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setTranscriptionPage(p => Math.max(1, p - 1))}
                  disabled={transcriptionPage === 1}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-300 bg-black border border-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-500 transition-colors"
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(conversations.length / TRANSCRIPTIONS_PER_PAGE) }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and adjacent pages
                      const totalPages = Math.ceil(conversations.length / TRANSCRIPTIONS_PER_PAGE);
                      return page === 1 || 
                             page === totalPages || 
                             Math.abs(page - transcriptionPage) <= 1;
                    })
                    .map((page, idx, arr) => (
                      <React.Fragment key={page}>
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="text-gray-500 px-1">...</span>
                        )}
                        <button
                          onClick={() => setTranscriptionPage(page)}
                          className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                            page === transcriptionPage
                              ? 'bg-white text-black font-medium'
                              : 'text-gray-300 hover:bg-white/10'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                </div>
                
                <button
                  onClick={() => setTranscriptionPage(p => Math.min(Math.ceil(conversations.length / TRANSCRIPTIONS_PER_PAGE), p + 1))}
                  disabled={transcriptionPage >= Math.ceil(conversations.length / TRANSCRIPTIONS_PER_PAGE)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-300 bg-black border border-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:border-gray-500 transition-colors"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Meetings Table */}
        {viewMode === 'list' && (
        <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <table className="w-full">
                  <thead className="bg-black border-b border-white/60 sticky top-0 z-10">
                    <tr>
                      <th 
                        className="text-left px-6 py-4 text-sm font-semibold text-white cursor-pointer hover:text-white bg-transparent"
                        onClick={() => handleSort('title')}
                      >
                        <div className="flex items-center gap-2">
                          Title
                          {sortField === 'title' && (
                            sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Agenda</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Organization</th>
                      <th 
                        className="text-left px-6 py-4 text-sm font-semibold text-white cursor-pointer hover:text-white whitespace-nowrap bg-transparent"
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center gap-2">
                          Date & Time
                          {sortField === 'date' && (
                            sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Type</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Format</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Transcription</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                {(() => {
                  // Sort list view meetings: Upcoming → TBD → Completed
                  const sortedListMeetings = [...filteredMeetings].sort((a, b) => {
                    const isValidDate = (dateStr) => {
                      if (!dateStr) return false;
                      const date = new Date(dateStr);
                      return !isNaN(date.getTime());
                    };
                    
                    const aHasDate = isValidDate(a.date);
                    const bHasDate = isValidDate(b.date);
                    
                    if (!aHasDate && !bHasDate) return 0;
                    if (!aHasDate) return a.status === 'done' ? 1 : 1;
                    if (!bHasDate) return b.status === 'done' ? -1 : -1;
                    
                    const aStatus = a.status || 'upcoming';
                    const bStatus = b.status || 'upcoming';
                    
                    if (aStatus === 'done' && bStatus !== 'done') return 1;
                    if (bStatus === 'done' && aStatus !== 'done') return -1;
                    
                    const aDate = parseLocalDate(a.date) || new Date(0);
                    const bDate = parseLocalDate(b.date) || new Date(0);
                    return aDate - bDate;
                  });
                  
                  if (sortedListMeetings.length === 0) {
                    return (
                      <tr>
                        <td colSpan="9" className="px-6 py-12 text-center text-white">
                          No {mainTab === 'meetings' ? 'meetings' : mainTab === 'projects' ? 'projects' : 'items'} found. Click &quot;{mainTab === 'meetings' ? 'New Meeting' : 'New Project'}&quot; to create one.
                        </td>
                      </tr>
                    );
                  }
                  
                  return sortedListMeetings.map((meeting) => {
                    const status = getMeetingDisplayStatus(meeting);
                    const primaryParticipant = meeting.participants && meeting.participants.length > 0 
                      ? meeting.participants[0] 
                      : null;
                    const meetingLinkType = getMeetingLinkType(meeting.meeting_link);

                    return (
                      <tr 
                        key={meeting.id} 
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedMeeting(meeting)}
                      >
                        {/* Title */}
                        <td className="px-6 py-4">
                          <div className="text-white font-medium">
                            {meeting.title || 'Untitled'}
                          </div>
                        </td>

                        {/* Agenda */}
                        <td className="px-6 py-4">
                          <div className="text-white text-sm">
                            {meeting.agenda ? (
                              <div className="line-clamp-2">
                                {meeting.agenda}
                              </div>
                            ) : (
                              <span className="text-white">-</span>
                            )}
                          </div>
                        </td>

                        {/* Organization */}
                        <td className="px-6 py-4">
                          <div className="text-white">
                            {primaryParticipant?.company || '-'}
                          </div>
                          {primaryParticipant?.role && (
                            <div className="text-xs text-white">{primaryParticipant.role}</div>
                          )}
                        </td>

                        {/* Date & Time (CST) */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{formatDate(meeting.date)}</div>
                          <div className="text-sm text-white">
                            {formatTimeCST(meeting.time)} ({meeting.duration || 60} min) CST
                          </div>
                        </td>

                        {/* Item Type - Project or Meeting */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            meeting.type === 'meeting' 
                              ? 'bg-blue-600/30 text-blue-300' 
                              : 'bg-purple-600/30 text-purple-300'
                          }`}>
                            {meeting.type === 'meeting' ? '📅 Meeting' : '📁 Project'}
                          </span>
                        </td>

                        {/* Format - Virtual/In-Person */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          {meeting.meeting_type === 'virtual' && meeting.meeting_link ? (
                            <a
                              href={meeting.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-white hover:text-gray-200 transition-colors"
                            >
                              <Video size={16} />
                              <span>{meetingLinkType} Link</span>
                              <ExternalLink size={14} />
                            </a>
                          ) : meeting.meeting_type === 'virtual' ? (
                            <div className="flex items-center gap-2">
                              <Video size={16} className="text-white" />
                              <span className="text-white">Virtual</span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-green-400" />
                                <span className="text-white">In-Person</span>
                              </div>
                              {meeting.location && (
                                <div className="text-xs text-white mt-1">{meeting.location}</div>
                              )}
                            </>
                          )}
                        </td>

                        {/* Transcription */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          {meeting.transcript ? (
                            <button
                              onClick={() => setViewingTranscription(meeting)}
                              className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                            >
                              <FileText size={16} />
                              <span>Yes</span>
                            </button>
                          ) : (
                            <span className="text-white">No</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                            status === 'upcoming' ? 'bg-transparent border-white/60 text-white' :
                            status === 'in_progress' ? 'bg-green-600/30 text-green-300 animate-pulse border-green-500/30' :
                            'bg-transparent border-white/60 text-white'
                          }`}>
                            {status === 'upcoming' ? 'Upcoming' : status === 'in_progress' ? 'In Progress' : 'Completed'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(meeting)}
                              className="text-white hover:text-gray-200 transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(meeting.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        )}

          </div>
          {/* End Column */}
        </div>
        {/* End Layout */}

        {/* Create/Edit Modal */}
        {(isCreating || isEditing) && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">
                    {isEditing 
                      ? (formData.type === 'meeting' ? 'Edit Meeting' : 'Edit Project')
                      : (formData.type === 'meeting' ? 'New Meeting' : 'New Project')
                    }
                  </h2>
                  <button onClick={resetForm} className="text-white hover:text-white">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Type Selector */}
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: 'project' })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        formData.type === 'project'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white/10 text-gray-400 hover:text-white'
                      }`}
                    >
                      📁 Project
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: 'meeting' })}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        formData.type === 'meeting'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-gray-400 hover:text-white'
                      }`}
                    >
                      📅 Meeting
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Project/Meeting Name */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        {formData.type === 'meeting' ? 'Meeting Title' : 'Project Name'} *
                      </label>
                      <input
                        type="text"
                        required
                        data-testid="project-title-input"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        placeholder={formData.type === 'meeting' ? 'e.g., Weekly Team Sync' : 'e.g., Website Redesign'}
                      />
                    </div>

                    {/* Status / Column */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Status *
                      </label>
                      <select
                        data-testid="project-status-input"
                        value={formData.pipeline_stage}
                        onChange={(e) => setFormData({ ...formData, pipeline_stage: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        <option value="tbd" className="bg-gray-900">TBD</option>
                        <option value="upcoming" className="bg-gray-900">Upcoming</option>
                        <option value="in_progress" className="bg-gray-900">In Progress</option>
                        <option value="done" className="bg-gray-900">Done</option>
                      </select>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Priority
                      </label>
                      <select
                        data-testid="project-priority-input"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        <option value="low" className="bg-gray-900">Low</option>
                        <option value="medium" className="bg-gray-900">Medium</option>
                        <option value="high" className="bg-gray-900">High</option>
                        <option value="urgent" className="bg-gray-900">Urgent</option>
                      </select>
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        data-testid="project-date-input"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500 [color-scheme:dark]"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Category
                      </label>
                      <select
                        data-testid="project-category-input"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        {categories.filter(c => c !== 'All').map(cat => (
                          <option key={cat} value={cat} className="bg-gray-900">{cat}</option>
                        ))}
                      </select>
                    </div>

                    {/* Location */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        data-testid="project-location-input"
                        value={formData.location || ''}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        placeholder="e.g., Conference Room A, Zoom, New York Office…"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Description / Agenda
                    </label>
                    <textarea
                      data-testid="project-description-input"
                      value={formData.agenda}
                      onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="Describe the project goals and scope..."
                    />
                  </div>

                  {/* Team Members */}
                  <div className="border-t border-white/10 pt-4">
                    <label className="block text-sm font-medium text-white mb-3">
                      Team Members
                    </label>
                    
                    {/* Add Team Member Form */}
                    <div className="bg-white/5 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Name *"
                          value={newParticipant.name}
                          onChange={(e) => setNewParticipant({ ...newParticipant, name: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={newParticipant.email}
                          onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Role/Title"
                          value={newParticipant.role}
                          onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addParticipant}
                        className="text-sm bg-white hover:bg-gray-100 text-black px-4 py-2 rounded transition-colors"
                      >
                        Add Member
                      </button>
                    </div>

                    {/* Team Members List */}
                    {formData.participants.length > 0 && (
                      <div className="space-y-2">
                        {formData.participants.map((participant, index) => (
                          <div key={index} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                {participant.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <div className="text-white text-sm font-medium">{participant.name}</div>
                                {participant.role && <div className="text-xs text-gray-400">{participant.role}</div>}
                              </div>
                            </div>
                            <button type="button" onClick={() => removeParticipant(index)} className="text-red-400 hover:text-red-300">
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* File Upload Section */}
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <label className="block text-sm font-medium text-white mb-3">
                      Attachments
                    </label>
                    
                    {/* Upload Button */}
                    <div className="flex items-center gap-3 mb-3">
                      <label className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-lg cursor-pointer transition-colors">
                        <Upload size={16} className="text-white" />
                        <span className="text-sm text-white">
                          {uploadingFormDoc ? 'Uploading...' : 'Upload Files'}
                        </span>
                        <input
                          type="file"
                          multiple
                          onChange={handleFormDocumentUpload}
                          disabled={uploadingFormDoc}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.mp3,.mp4,.wav,.webm"
                        />
                      </label>
                      {uploadingFormDoc && <Loader2 className="w-4 h-4 animate-spin text-white" />}
                      <span className="text-xs text-gray-400">
                        PDF, DOC, XLS, PPT, Images, Audio
                      </span>
                    </div>
                    
                    {/* Uploaded Files List */}
                    {formDocuments.length > 0 && (
                      <div className="space-y-2">
                        {formDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between bg-white/5 border border-white/20 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText size={16} className="text-purple-400 shrink-0" />
                              <span className="text-sm text-white truncate">{doc.original_filename}</span>
                              <span className="text-xs text-gray-500 shrink-0">{doc.size}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFormDocument(doc.id)}
                              className="text-red-400 hover:text-red-300 shrink-0 ml-2"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      data-testid="create-project-btn"
                      className="flex-1 bg-white hover:bg-gray-100 text-black py-3 rounded-lg font-medium transition-colors"
                    >
                      {isEditing ? 'Update Project' : 'Create Project'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 bg-transparent hover:bg-white/5 text-white rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Meeting/Project Details Modal */}
        {selectedMeeting && (
          <ProjectDetailPanel
            meeting={selectedMeeting}
            conversations={conversations}
            meetingTranscriptions={meetingTranscriptions}
            setMeetingTranscriptions={setMeetingTranscriptions}
            setViewingTranscription={setViewingTranscription}
            setShowTranscriptionPopup={setShowTranscriptionPopup}
            uploadingDocument={uploadingDocument}
            documentUploadMeetingId={documentUploadMeetingId}
            handleDocumentUpload={handleDocumentUpload}
            handleRemoveDocument={handleRemoveDocument}
            getMeetingStatus={getMeetingStatus}
            formatDate={formatDate}
            formatTimeTo12Hour={formatTimeTo12Hour}
            getMeetingLinkType={getMeetingLinkType}
            getDocumentIcon={getDocumentIcon}
            apiUrl={apiUrl}
            onClose={() => { setSelectedMeeting(null); setSelectedOrgMeetings([]); }}
            onEdit={() => { setSelectedMeeting(null); setSelectedOrgMeetings([]); handleEdit(selectedMeeting); }}
            onNotesUpdate={async (meetingId, updatedNotes) => {
              try {
                await apiCall(`/meetings/${meetingId}`, {
                  method: 'PUT',
                  body: JSON.stringify({ panel_notes: JSON.stringify(updatedNotes) })
                });
                // Keep selectedMeeting in sync so notes survive re-opens
                setSelectedMeeting(prev => prev ? { ...prev, panel_notes: JSON.stringify(updatedNotes) } : prev);
                setMeetings(prev => prev.map(m => m.id === meetingId ? { ...m, panel_notes: JSON.stringify(updatedNotes) } : m));
              } catch (err) {
                console.error('Failed to save notes:', err);
              }
            }}
            onTranscriptionUpdate={async (meetingId, transcriptionId) => {
              try {
                await apiCall(`/meetings/${meetingId}`, {
                  method: 'PUT',
                  body: JSON.stringify({ transcription_id: transcriptionId })
                });
                setMeetings(prev => prev.map(m => m.id === meetingId ? { ...m, transcription_id: transcriptionId } : m));
              } catch (err) {
                console.error('Failed to save transcription link:', err);
              }
            }}
          />
        )}

        {/* Expanded Conversation Modal */}
        {expandedConversation && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black border-2 border-white/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  {conversationEditMode ? (
                    <input
                      type="text"
                      value={editConversationData.title}
                      onChange={(e) => setEditConversationData(prev => ({ ...prev, title: e.target.value }))}
                      className="text-2xl font-bold text-white bg-white/10 border border-white/30 rounded-lg px-3 py-1 flex-1 mr-4"
                      placeholder="Conversation title"
                    />
                  ) : (
                    <h2 className="text-2xl font-bold text-white">{expandedConversation.title}</h2>
                  )}
                  <div className="flex items-center gap-2">
                    {!conversationEditMode && (
                      <>
                        <button
                          onClick={() => {
                            setConversationEditMode(true);
                            setEditConversationData({
                              title: expandedConversation.title || '',
                              category: expandedConversation.category || 'Meetings'
                            });
                          }}
                          className="p-2 hover:bg-white/10 rounded-lg text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={20} />
                        </button>
                        <button
                          onClick={() => deleteConversation(expandedConversation.id)}
                          className="p-2 hover:bg-white/10 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={20} />
                        </button>
                      </>
                    )}
                    <button onClick={() => { setExpandedConversation(null); setConversationEditMode(false); }} className="text-white hover:text-gray-300">
                      <X size={24} />
                    </button>
                  </div>
                </div>

                {/* Category Badge / Selector */}
                <div className="mb-4">
                  {conversationEditMode ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Category:</span>
                      <select
                        value={editConversationData.category}
                        onChange={(e) => setEditConversationData(prev => ({ ...prev, category: e.target.value }))}
                        className="px-3 py-1.5 bg-white/10 border border-white/30 text-white rounded-lg text-sm focus:outline-none focus:border-purple-500"
                      >
                        <option value="Meetings">Meetings</option>
                        <option value="Projects">Projects</option>
                      </select>
                    </div>
                  ) : (
                    <span className="px-3 py-1.5 bg-purple-600/30 text-purple-300 rounded-full text-sm">
                      {expandedConversation.category || 'Meetings'}
                    </span>
                  )}
                </div>

                {/* Edit Mode Save/Cancel Buttons */}
                {conversationEditMode && (
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => updateConversation(expandedConversation.id, editConversationData)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setConversationEditMode(false)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar size={16} />
                    {(() => {
                      const dateStr = expandedConversation.created_at || expandedConversation.date;
                      if (!dateStr) return 'No date';
                      const date = new Date(dateStr);
                      if (isNaN(date.getTime())) return 'No date';
                      return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    })()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={16} />
                    {expandedConversation.duration ? formatTime(Math.floor(expandedConversation.duration)) : '0:00'}
                  </span>
                </div>

                {/* AI Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-3">AI Summary</h3>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-gray-300 text-sm">
                      {expandedConversation.ai_summary || expandedConversation.summary || 'No summary available'}
                    </pre>
                  </div>
                </div>

                {/* Full Transcript as Chat */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-purple-400 mb-3">Full Conversation</h3>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                    {expandedConversation.transcript ? (
                      parseTranscriptToMessages(expandedConversation.transcript).map((msg, idx) => (
                        <div key={idx} className="text-sm">
                          <div className="font-semibold text-cyan-400 mb-1">{msg.speaker}:</div>
                          <div className="text-gray-300 pl-4">{msg.text}</div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 italic">No transcript available</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <button
                    onClick={() => { setExpandedConversation(null); setConversationEditMode(false); }}
                    className="w-full bg-transparent hover:bg-white/5 text-white py-3 rounded-lg font-medium transition-colors border border-white/20"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Category Management Modal */}
        {showMeetingCategoryModal && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowMeetingCategoryModal(false)}
          >
            <div 
              className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Edit Meeting Categories</h3>
                <button
                  onClick={() => setShowMeetingCategoryModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  data-testid="close-meeting-category-modal-btn"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
              
              {/* Add New Category */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex gap-2 mb-3">
                  {/* Icon Selector */}
                  <div className="relative">
                    <button
                      type="button"
                      className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                      onClick={(e) => {
                        const dropdown = e.currentTarget.nextElementSibling;
                        dropdown.classList.toggle('hidden');
                      }}
                      data-testid="meeting-category-icon-selector"
                    >
                      {newMeetingCategoryIcon}
                    </button>
                    <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                      {meetingCategoryIcons.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={(e) => {
                            setNewMeetingCategoryIcon(icon);
                            e.currentTarget.parentElement.classList.add('hidden');
                          }}
                          className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={newMeetingCategoryName}
                    onChange={(e) => setNewMeetingCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addMeetingCategory()}
                    placeholder="New category name..."
                    className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="new-meeting-category-input"
                  />
                </div>
                
                {/* Color Selector */}
                <div className="flex gap-2 items-center mb-3">
                  <span className="text-xs text-gray-400">Color:</span>
                  <div className="flex gap-1 flex-wrap">
                    {meetingCategoryColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewMeetingCategoryColor(color)}
                        className={`w-6 h-6 rounded-full ${getMeetingCategoryColorClasses(color).split(' ')[0]} ${newMeetingCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : 'hover:opacity-80'}`}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={addMeetingCategory}
                  disabled={!newMeetingCategoryName.trim()}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                  data-testid="add-meeting-category-btn"
                >
                  <Plus size={14} />
                  Add Category
                </button>
              </div>
              
              {/* Category List */}
              <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                {customMeetingCategories.filter(c => c.id !== 'all').map((category) => {
                  const protectedCategories = ['real-estate', 'personal'];
                  const isProtected = protectedCategories.includes(category.id);
                  
                  return (
                    <div 
                      key={category.id}
                      className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                    >
                      {editingMeetingCategory === category.id ? (
                        <>
                          <span className="text-lg">{category.icon}</span>
                          <input
                            type="text"
                            value={editMeetingCategoryValue}
                            onChange={(e) => setEditMeetingCategoryValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') updateMeetingCategory(category.id, editMeetingCategoryValue, category.icon, category.color);
                              if (e.key === 'Escape') {
                                setEditingMeetingCategory(null);
                                setEditMeetingCategoryValue('');
                              }
                            }}
                            className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                            autoFocus
                            data-testid={`edit-meeting-category-input-${category.id}`}
                          />
                          <button
                            onClick={() => updateMeetingCategory(category.id, editMeetingCategoryValue, category.icon, category.color)}
                            className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                            title="Save"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingMeetingCategory(null);
                              setEditMeetingCategoryValue('');
                            }}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className={`w-8 h-8 rounded-lg ${getMeetingCategoryColorClasses(category.color).split(' ')[0]} flex items-center justify-center text-sm`}>
                            {category.icon}
                          </span>
                          <span className="flex-1 text-white text-sm">
                            {category.name}
                            {isProtected && (
                              <span className="ml-2 text-xs text-gray-500">(default)</span>
                            )}
                          </span>
                          <button
                            onClick={() => {
                              setEditingMeetingCategory(category.id);
                              setEditMeetingCategoryValue(category.name);
                            }}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit"
                            data-testid={`edit-meeting-category-btn-${category.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          {!isProtected && (
                            <button
                              onClick={() => deleteMeetingCategory(category.id)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete"
                              data-testid={`delete-meeting-category-btn-${category.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => setShowMeetingCategoryModal(false)}
                  className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                  data-testid="done-meeting-category-btn"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Google Sheets Modal */}
        {showSheetsModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg" data-testid="google-sheets-modal">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-green-400">
                      <path d="M19 3H5C3.89 3 3 3.89 3 5V19C3 20.11 3.89 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.89 20.11 3 19 3M19 19H5V5H19V19M7 7H17V9H7V7M7 11H17V13H7V11M7 15H14V17H7V15" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Google Sheets Sync</h2>
                    <p className="text-sm text-gray-400">Two-way sync for meetings & projects</p>
                  </div>
                </div>
                <button onClick={() => setShowSheetsModal(false)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Connection Status */}
                <div className={`p-4 rounded-xl border ${sheetsConnected ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-800 border-gray-700'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${sheetsConnected ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <span className="text-white font-medium">
                        {sheetsConnected ? 'Google Account Connected' : 'Not Connected'}
                      </span>
                    </div>
                    {sheetsConnected ? (
                      <button
                        onClick={handleDisconnectSheets}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button
                        onClick={handleConnectGoogleSheets}
                        className="px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-200"
                      >
                        Connect Google
                      </button>
                    )}
                  </div>
                </div>
                
                {sheetsConnected && (
                  <>
                    {/* Add Spreadsheet */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-white">Connect a Spreadsheet</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={spreadsheetId}
                          onChange={(e) => setSpreadsheetId(e.target.value)}
                          placeholder="Paste Spreadsheet ID from URL"
                          data-testid="spreadsheet-id-input"
                          className="flex-1 px-4 py-2.5 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-gray-500"
                        />
                        <button
                          onClick={handleConnectSpreadsheet}
                          disabled={sheetsLoading || !spreadsheetId.trim()}
                          data-testid="connect-spreadsheet-btn"
                          className="px-4 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {sheetsLoading ? <Loader2 size={16} className="animate-spin" /> : 'Connect'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Find the ID in your Google Sheets URL: docs.google.com/spreadsheets/d/<span className="text-green-400">[SPREADSHEET_ID]</span>/edit
                      </p>
                    </div>
                    
                    {/* Connected Spreadsheets */}
                    {sheetConnections.length > 0 && (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-white">Connected Spreadsheets</label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {sheetConnections.map((conn) => (
                            <div key={conn.id} className="p-3 bg-black border border-gray-700 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-white font-medium text-sm truncate">{conn.spreadsheet_title}</span>
                                {conn.last_sync && (
                                  <span className="text-xs text-gray-500">
                                    Synced: {new Date(conn.last_sync).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleImportFromSheets(conn)}
                                  disabled={syncingSheets}
                                  data-testid={`import-sheets-${conn.id}`}
                                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs hover:bg-blue-500/30 disabled:opacity-50"
                                >
                                  <Download size={14} />
                                  Import
                                </button>
                                <button
                                  onClick={() => handleExportToSheets(conn)}
                                  disabled={syncingSheets}
                                  data-testid={`export-sheets-${conn.id}`}
                                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg text-xs hover:bg-green-500/30 disabled:opacity-50"
                                >
                                  <Upload size={14} />
                                  Export
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Column Format Info */}
                    <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                      <h4 className="text-sm font-medium text-white mb-2">Expected Column Format</h4>
                      <p className="text-xs text-gray-400 mb-2">Your Google Sheet should have these columns in the first row:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {['Title', 'Date', 'Time', 'End Time', 'Location', 'Description', 'Attendees', 'Category', 'Status'].map(col => (
                          <span key={col} className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                            {col}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => setShowSheetsModal(false)}
                  className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Document Upload Modal */}
        {showDocumentUploadModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg" data-testid="document-upload-modal">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Upload size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Upload Meetings</h2>
                    <p className="text-sm text-gray-400">Import from CSV, PDF, or image</p>
                  </div>
                </div>
                <button onClick={handleCloseDocumentUploadModal} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="p-6">
                {/* Upload Area */}
                {!parsedMeetingsFromDoc && (
                  <div className="space-y-4">
                    <div 
                      onClick={() => documentFileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                    >
                      {documentUploadProgress ? (
                        <div className="space-y-3">
                          <div className="w-12 h-12 mx-auto border-4 border-gray-600 border-t-white rounded-full animate-spin" />
                          <p className="text-gray-400">
                            {documentUploadProgress === 'reading' && 'Reading file...'}
                            {documentUploadProgress === 'parsing' && 'Extracting meetings with AI...'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload size={48} className="mx-auto text-gray-500 mb-4" />
                          <p className="text-white font-medium mb-2">Click to upload or drag and drop</p>
                          <p className="text-gray-500 text-sm">Supports PDF, CSV, JPG, PNG, WebP (Max 10MB)</p>
                        </>
                      )}
                    </div>
                    
                    <input
                      ref={documentFileInputRef}
                      type="file"
                      accept=".pdf,.csv,.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp,application/pdf,text/csv"
                      onChange={handleDocumentFileSelect}
                      className="hidden"
                      data-testid="meeting-file-upload-input"
                    />
                    
                    <div className="flex items-start gap-3 p-4 bg-black rounded-lg">
                      <FileText size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white text-sm font-medium">Supported Formats</p>
                        <p className="text-gray-500 text-xs mt-1">
                          <strong>CSV:</strong> Title, Date, Time, End Time, Location, Description, Attendees, Category, Status<br/>
                          <strong>PDF/Images:</strong> Calendar screenshots, meeting schedules, event lists
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Parsed Results */}
                {parsedMeetingsFromDoc && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-400 mb-4">
                      <CheckCircle size={20} />
                      <span>Found {parsedMeetingsFromDoc.length} meeting(s)</span>
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto space-y-3">
                      {parsedMeetingsFromDoc.map((meeting, idx) => (
                        <div key={idx} className="p-4 bg-black rounded-lg border border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{meeting.title || 'Untitled Meeting'}</span>
                            {meeting.date && (
                              <span className="text-xs text-gray-400">{meeting.date}</span>
                            )}
                          </div>
                          {meeting.time && (
                            <p className="text-gray-400 text-sm flex items-center gap-1">
                              <Clock size={12} />
                              {meeting.time}{meeting.end_time ? ` - ${meeting.end_time}` : ''}
                            </p>
                          )}
                          {meeting.location && (
                            <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                              <MapPin size={12} />
                              {meeting.location}
                            </p>
                          )}
                          {meeting.attendees && (
                            <p className="text-gray-500 text-xs mt-2">{meeting.attendees}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleImportParsedMeetings}
                        disabled={documentUploadProgress === 'importing'}
                        data-testid="import-meetings-btn"
                        className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        {documentUploadProgress === 'importing' ? 'Importing...' : `Import ${parsedMeetingsFromDoc.length} Meeting(s)`}
                      </button>
                      <button
                        onClick={() => setParsedMeetingsFromDoc(null)}
                        className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealEstateManagingPartnerMeetings;
